import { useWhatsAppWritePermission } from '@/hooks/useWhatsAppWritePermission';
import { FinalizarTarefaNaNota } from '@/components/tarefas/FinalizarTarefaNaNota';
import { CicloQualidadeFaixa } from './CicloQualidadeFaixa';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { createOggRecorder, isManualAudioSupported, startOggRecording } from '@/utils/audioConverter';
import type Recorder from 'opus-recorder';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useCorretorTexto } from '@/hooks/useCorretorTexto';
import { CorretorSugestoes } from './CorretorSugestoes';
import { WhatsAppAvatar } from './WhatsAppAvatar';
import { cn } from '@/lib/utils';
import { sendWhatsAppMessage } from '@/lib/whatsappSender';
import { formatConnectionName } from './connectionBadgeColors';
import { ModalCriarLeadManual } from '@/components/leads-carteira/ModalCriarLeadManual';
import { useConversaCartao } from '@/hooks/useConversaCartao';
import { useDicionarioSituacao, chaveSituacao } from '@/hooks/useDicionarioSituacao';
import { rotuloColuna } from '@/lib/quadro-utils';
import { getAgenteNomeCurto, getAgenteEspecialidade, getAgenteCores } from './agenteIaLabels';
import { useCampanhasIaOnly } from '@/hooks/useConversations';
import { callRpc } from '@/lib/callRpc';
import { ImagePreviewModal } from '@/components/anexos/ImagePreviewModal';
import {
  Check,
  CheckCheck,
  AlertCircle,
  Send,
  Loader2,
  Plus,
  FileUp,
  ImageIcon,
  Mic,
  Zap,
  FileText,
  ChevronUp,
  AlertTriangle,
  ArrowLeft,
  ImageOff,
  Trash2,
  BotMessageSquare,
  MessageSquare,
  Lock,
  UserPlus,
  CircleDot,
  Copy,
  Library,
  Pencil,
  X as XIcon,
  CornerUpLeft,
  SmilePlus,
  Link2,
  ShieldCheck,
  Clock,
  CheckCircle2,
  ArrowRightLeft,
  PanelRightOpen,
} from 'lucide-react';
import CatalogoPicker from './CatalogoPicker';
import { useMessages } from '@/hooks/useMessages';
import {
  useReacoesConversa,
  useReagirMensagem,
  useLeiturasGrupo,
  useMarcarGrupoLido,
  useApagarMensagem,
  useChatGrupoMembros,
  useTypingGrupo,
  emitirTypingGrupo,
  emitirPresencaDigitando,
  useReagirMensagemReal,
  notificarMencoes,
  useChatGrupos,
  useAutoresGrupoWhats,
  lerAutorGrupoWhats,
  chaveTelefoneGrupo,
  type AutorGrupoWhats,
  type ReacaoMensagem,
} from '@/hooks/useChatGrupos';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useMinhaConexaoSaude } from '@/hooks/useMinhaConexaoSaude';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMessageSenders, getSenderColorClass, getSenderInitials } from '@/hooks/useMessageSenders';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import type { Conversation } from '@/hooks/useConversations';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TemplateSelectorPopover } from './TemplateSelectorPopover';
import {
  describeWhatsAppError,
  codigoExibidoWhatsApp,
  whatsAppErrorToToast,
  severityLabel,
  formatConnectionPhone,
  buildIncidentText,
} from '@/lib/whatsappErrorMessages';
import { useConnectionHealth } from '@/hooks/useConnectionHealth';
import { useEvolutionHealth } from '@/hooks/useEvolutionHealth';
import { getWindowExpiresAt, getWindowGhost } from './whatsappWindow';
import { ComposerTemplateOficial } from './ComposerTemplateOficial';
import { useSaudeConexoes } from '@/hooks/useSaudeConexoes';
import EvolutionDownBanner from './EvolutionDownBanner';
import { AudioPlayer } from '@/components/common/AudioPlayer';
// Janela 24h / template (WhatsApp Oficial/360dialog) ABOLIDA — inbox é Evolution-only.
import { getDestinationPhone } from '@/utils/phone-canonical';
import { postConversationAction } from '@/lib/whatsappConversationActions';
import { servidorEstaFora, esperarServidorVoltar } from '@/lib/servidorFora';

function formatMsgTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Remove marcadores internos de idempotência ([ref:..] [fid:..] [cmp:..])
 * que vivem no body persistido para deduplicação no backend, mas não devem
 * aparecer na UI da inbox. O cliente final NUNCA recebe essas tags
 * (templates HSM são renderizados pela Meta a partir das variáveis).
 */
/** Destaca @menções no texto de grupo (negrito azul, estilo WhatsApp). */
function renderTextoComMencoes(texto: string): React.ReactNode {
  const partes = texto.split(/(@[\wÀ-ÿ]+)/g);
  if (partes.length === 1) return texto;
  return partes.map((parte, i) =>
    parte.startsWith('@') ? (
      <span key={i} className="font-semibold text-primary">{parte}</span>
    ) : (
      <React.Fragment key={i}>{parte}</React.Fragment>
    ),
  );
}

function primeiroNome(nome: string | null | undefined): string {
  return (nome || '').trim().split(/\s+/)[0] || 'Alguém';
}

function stripFaturaTags(body: string | null | undefined): string {
  if (!body) return '';
  return body
    .replace(/\s*\[(?:ref|fid|cmp):[^\]]*\]/gi, '')
    // Gatilho de automação do agente IA: a regra de fechamento precisa do
    // marcador [FECHAMENTO_VENDA] no body para ser disparada no backend,
    // mas o JSON técnico que vem depois polui a visão do operador. Mantemos
    // apenas o rótulo limpo "🔒 FECHAMENTO DE VENDA".
    .replace(/\[FECHAMENTO_VENDA\]\s*(\{[\s\S]*\})?/g, '🔒 FECHAMENTO DE VENDA')
    // Nota interna de transferencia IA->humano: a tool transferir_humano grava
    // o body com cabecalho tecnico "[TRANSFERENCIA IA->HUMANO]\nMotivo:...\n
    // Urgencia:...\nResumo:..." pra audit. Visualmente ja temos o destaque
    // de nota privada (fundo vermelho + lock). Trocamos o cabecalho ruidoso
    // por um titulo limpo e mantemos o resumo.
    .replace(/^\[TRANSFERENCIA IA->HUMANO\]\s*\n/g, '🔁 Transferência da IA pra humano\n\n')
    // Cron de reassumir tem padrao parecido:
    .replace(/^\[IA REASSUMIU APOS 2H\]\s*\n/g, 'IA reassumiu após 2h sem resposta\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const STATUS_LABELS: Record<string, string> = {
  nova: 'Nova',
  em_atendimento: 'Em Atendimento',
  aguardando_cliente: 'Aguard. Cliente',
  aguardando_interno: 'Aguard. Interno',
  convertida: 'Convertida',
  finalizada: 'Finalizada',
  arquivada: 'Arquivada',
};

function formatDateSeparator(dateStr: string): string {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Hoje';
  if (d.toDateString() === yesterday.toDateString()) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function StatusIcon({ status }: { status: string | null }) {
  if (status === 'read') return <CheckCheck className="h-3 w-3 text-primary" />;
  if (status === 'delivered') return <CheckCheck className="h-3 w-3 text-muted-foreground" />;
  if (status === 'sent') return <Check className="h-3 w-3 text-muted-foreground" />;
  if (status === 'failed') return <AlertCircle className="h-3 w-3 text-destructive" />;
  if (status === 'pending') return <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />;
  return <Check className="h-3 w-3 text-muted-foreground" />;
}

function ImageBubble({ url, isOut, caption, onPreview, onVincularChip }: { url: string; isOut: boolean; caption?: string | null; onPreview?: (url: string) => void; onVincularChip?: () => void }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className="space-y-1">
      {error ? (
        <div className={cn(
          'flex flex-col items-center justify-center gap-1.5 rounded-xl p-4 w-48 h-32',
          isOut
            ? 'bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-primary/30'
            : 'bg-muted/60 border border-border'
        )}>
          <ImageOff className="h-6 w-6 text-muted-foreground" />
          <span className="text-[10px] text-muted-foreground text-center">Imagem indisponível</span>
        </div>
      ) : (
        <div className="relative">
          {!loaded && (
            <div className={cn(
              'flex items-center justify-center rounded-xl w-48 h-32',
              isOut ? 'bg-primary/10' : 'bg-muted/40'
            )}>
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          <img
            src={url}
            alt="Imagem"
            className={cn(
              'rounded-xl max-h-52 max-w-[240px] object-cover cursor-pointer shadow-sm transition-opacity duration-200',
              isOut
                ? 'border-2 border-primary/20 dark:border-primary/30'
                : 'border-2 border-border',
              loaded ? 'opacity-100' : 'opacity-0 absolute inset-0'
            )}
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
            onClick={() => onPreview ? onPreview(url) : window.open(url, '_blank')}
          />
          {loaded && onVincularChip && <button type="button" title="Vincular foto a uma linha do pedido" aria-label="Vincular foto a uma linha do pedido"
            className="absolute bottom-2 right-2 rounded-md border bg-background/95 p-1.5 text-primary shadow-sm hover:bg-muted"
            onClick={e => { e.stopPropagation(); onVincularChip(); }}><Link2 className="h-3.5 w-3.5" /></button>}
        </div>
      )}
      {caption && (
        <p className="text-xs text-muted-foreground">
          {caption}
        </p>
      )}
    </div>
  );
}

// Sem cauda SVG — usamos canto assimétrico (rounded-tr-md / rounded-tl-md)
// que dá um look moderno/cinematográfico (estilo iMessage 2025).

export function MessageBubble({
  msg,
  prevMsg,
  somenteLeitura,
  onPreview,
  senders,
  agenteIaNome,
  isGrupo,
  meuUserId,
  grupo,
  autoresGrupoWhats,
}: {
  msg: ReturnType<typeof useMessages>['messages'][0];
  /** Mensagem anterior do MESMO dia — usada pra agrupar sequência do mesmo autor
   *  (cauda + espaçamento apertado + assinatura só na 1ª, igual WhatsApp). */
  prevMsg?: ReturnType<typeof useMessages>['messages'][0] | null;
  onPreview?: (url: string) => void;
  senders: Map<string, { id: string; nome: string | null; foto_url: string | null }>;
  /** Nome do agente IA (ex: "IsabellA") quando msg.message_source === 'agente_ia'.
   *  Renderizado em vermelho/negrito com ícone de robô em vez do label genérico. */
  agenteIaNome?: string | null;
  /** Em grupo interno todas as msgs são outbound, mas o lado deve seguir AUTOR:
   *  minhas msgs à direita (verde), msgs dos outros membros à esquerda (branco).
   *  Igual grupo de WhatsApp normal. */
  isGrupo?: boolean;
  meuUserId?: string | null;
  /** Só no grupo do WhatsApp: equipe (nome e cor) de cada autor do CRM. */
  autoresGrupoWhats?: Map<string, AutorGrupoWhats>;
  /** A página da Konnect mostra a conversa com a MESMA bolha, sem editar (24/10/2026). */
  somenteLeitura?: boolean;
  /** Extras de grupo interno: reações, responder, apagar, recibo e citação. */
  grupo?: {
    reacoes?: ReacaoMensagem[];
    onReply?: () => void;
    onReact?: (emoji: string) => void;
    onDelete?: () => void;
    podeApagar?: boolean;
    lidoPorTodos?: boolean;
    quoted?: { autor: string; snippet: string } | null;
  };
}) {
  // Em grupo: lado = autor é eu? Senão: lado = direction outbound.
  const isOut = isGrupo
    ? (msg.sent_by_user_id === meuUserId)
    : (msg.direction === 'outbound');
  const isNote = msg.is_private_note;
  const isHuman = msg.message_source === 'humano' || msg.message_source === null || msg.message_source === undefined ? msg.message_source === 'humano' : false;
  const sender = msg.sent_by_user_id ? senders.get(msg.sent_by_user_id) : null;
  // Continuação de sequência (igual WhatsApp): mesma direção/autor da anterior,
  // nenhuma das duas é nota, e menos de 5min de intervalo → bolha "colada"
  // (sem cauda, cantos cheios, espaçamento 3px, assinatura só na 1ª).
  const prevOut = prevMsg
    ? (isGrupo ? prevMsg.sent_by_user_id === meuUserId : prevMsg.direction === 'outbound')
    : null;
  const continuacao = !!prevMsg
    && !isNote
    && !prevMsg.is_private_note
    && prevOut === isOut
    && (!isGrupo || prevMsg.sent_by_user_id === msg.sent_by_user_id)
    && (new Date(msg.created_at).getTime() - new Date(prevMsg.created_at).getTime()) < 5 * 60 * 1000;
  // Mostra avatar+nome também em notas internas (Douglas 26/05: nota sem
  // identificar quem escreveu deixava equipe sem saber a fonte).
  const showHumanSignature = isOut && msg.message_source === 'humano' && !!sender && !continuacao;
  // Em grupo, msg de outro membro (à esquerda) também mostra nome+avatar
  // do autor no topo da bolha — igual grupo de WhatsApp normal.
  const showAuthorInGrupo = isGrupo && !isOut && !!sender && !continuacao;
  const autorExterno = (msg as { autor_externo?: { nome: string; telefone: string } | null }).autor_externo;
  const showAutorExterno = isGrupo && !isOut && !sender && !!autorExterno && !continuacao;
  const equipeAutor = autoresGrupoWhats && msg.sent_by_user_id ? autoresGrupoWhats.get(msg.sent_by_user_id) : null;
  const showAutoLabel = isOut && !isNote && msg.message_source && msg.message_source !== 'humano';
  const senderColor = getSenderColorClass(msg.sent_by_user_id);

  // POR ONDE A MENSAGEM PASSOU (Douglas 04/08): com a convivência Bee360 × Evolution,
  // olhar a bolha tem que responder "isso foi pela API oficial ou não?".
  const canalOrigem = (msg as { canal_origem?: string | null }).canal_origem;
  const canalSelo = canalOrigem === 'oficial'
    ? { texto: 'API oficial', title: 'Enviada/recebida pela API oficial do WhatsApp (Cloud API), via Bee360.', cor: 'text-primary dark:text-primary' }
    : canalOrigem === 'nao_oficial'
      ? { texto: 'Não oficial', title: 'Passou pelo canal não oficial (QR/Baileys), via Bee360.', cor: 'text-primary dark:text-primary' }
      : canalOrigem === 'evolution'
        ? { texto: 'Evolution', title: 'Passou pela Evolution (conexão por QR do próprio CRM).', cor: 'text-muted-foreground' }
        : isOut && msg.status !== 'failed'
          ? { texto: 'Confirmando…', title: 'Enviada; o provedor ainda não confirmou por qual porta saiu. Quando a janela de 24h está fechada, o Bee360 desvia para o canal não oficial — por isso a porta só é carimbada quando ele confirma.', cor: 'text-muted-foreground' }
          : null;

  // Edicao inline — WhatsApp permite editar so texto outbound dentro de 15min
  // pra propagar pro cliente. Fora disso, ainda editamos no CRM (audit) mas
  // o cliente continua vendo a versao original. Por isso o lapis aparece
  // sempre em outbound texto: a edge function decide o que faz no servidor.
  const editedAt = (msg as any).edited_at as string | null | undefined;
  const isTextLike = msg.type === 'text' || msg.type === 'template' || !msg.type;
  const ageMs = Date.now() - new Date(msg.created_at).getTime();
  const dentroJanela = ageMs < 14 * 60 * 1000;
  const isDeleted = !!(msg as any).deleted_at;
  const canEdit = !somenteLeitura && isOut && isTextLike && msg.status !== 'failed' && !isDeleted;
  const [isEditing, setIsEditing] = useState(false);
  const [reactOpen, setReactOpen] = useState(false);
  const [editText, setEditText] = useState(msg.body || '');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  async function handleSaveEdit() {
    const novo = editText.trim();
    if (!novo) {
      toast.error('Mensagem nao pode ficar vazia');
      return;
    }
    if (novo === (msg.body || '').trim()) {
      setIsEditing(false);
      return;
    }
    setIsSavingEdit(true);
    try {
      const { data, error } = await supabase.functions.invoke('whatsapp-evolution-edit', {
        body: { message_id: msg.id, new_text: novo },
      });
      if (error) throw new Error(error.message);
      if (data && data.success === false) throw new Error(data.error || 'Falha ao editar');
      // scope='whatsapp_e_crm' -> editou nos dois lados; 'so_crm' -> WhatsApp
      // ja nao aceitava (>15min ou sem provider_message_id), so atualizou
      // no banco; 'nota_interna' -> nota privada, nunca foi pro WhatsApp.
      const scope = data?.scope;
      if (scope === 'so_crm') {
        toast.success('Editada no CRM — cliente continua vendo a versao original (passou de 15min do WhatsApp).');
      } else if (scope === 'nota_interna') {
        toast.success('Nota interna editada');
      } else {
        toast.success('Mensagem editada (cliente ve a tag "editada")');
      }
      setIsEditing(false);
    } catch (e: any) {
      toast.error('Erro ao editar: ' + (e?.message || String(e)));
    } finally {
      setIsSavingEdit(false);
    }
  }

  return (
    <div className={cn('flex group', continuacao ? 'mt-[3px]' : 'mt-2.5', isOut ? 'justify-end' : 'justify-start')}>
      <div className={cn('relative max-w-[75%] animate-in fade-in slide-in-from-bottom-1 duration-200', isOut ? 'pr-1' : 'pl-1')}>
        {/* Cauda da bolha (só na 1ª da sequência) — triângulo colado no canto,
            mesma cor da bolha, apontando pra fora igual WhatsApp. */}
        {!isNote && !continuacao && (isOut ? (
          <span aria-hidden className="absolute right-0 top-0 h-[10px] w-[10px] bg-primary/[0.07] dark:bg-primary/[0.18] [clip-path:polygon(0%_0%,100%_0%,0%_100%)]" />
        ) : (
          <span aria-hidden className="absolute left-0 top-0 h-[10px] w-[10px] bg-card [clip-path:polygon(0%_0%,100%_0%,100%_100%)]" />
        ))}
        <div
          className={cn(
            'rounded-2xl px-3.5 py-2',
            'shadow-[0_1px_2px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)]',
            'tracking-[-0.005em]',
            isNote
              ? 'bg-primary/10 text-foreground ring-1 ring-primary/40 rounded-2xl'
              : isOut
                ? cn('bg-primary/[0.07] text-foreground ring-1 ring-primary/10 dark:bg-primary/[0.18] dark:ring-primary/25', !continuacao && 'rounded-tr-[3px]')
                : cn('bg-card text-foreground ring-1 ring-border', !continuacao && 'rounded-tl-[3px]'),
            msg.status === 'failed' && 'opacity-60'
          )}
        >
        {/* Selo de nota interna — cadeado + aviso sutil para diferenciar de mensagem ao cliente */}
        {isNote && (
          <div className="mb-1 flex items-center gap-1 text-primary">
            <Lock className="h-2.5 w-2.5" strokeWidth={2.5} />
            <span className="text-[10px] font-semibold uppercase tracking-wider not-italic">
              Nota interna · só CRM
            </span>
          </div>
        )}
        {/* Assinatura humana (estilo grupo do WhatsApp) — avatar do
            operador que escreveu. Padronizado em h-6 (24px) — Douglas 24/05:
            "padrão com a lista" — antes era 16px (h-4), visualmente apertado. */}
        {showHumanSignature && (
          <div className="flex items-center gap-1.5 mb-0.5">
            <Avatar className="h-6 w-6">
              <AvatarImage src={sender?.foto_url || undefined} />
              <AvatarFallback className="text-[10px]">{getSenderInitials(sender?.nome)}</AvatarFallback>
            </Avatar>
            <span className={cn('text-[11px] font-semibold opacity-90 truncate max-w-[180px]', senderColor)}>
              {sender?.nome || 'Consultor'}
            </span>
          </div>
        )}
        {/* Em grupo: nome + avatar do autor no topo da msg dos outros membros */}
        {showAuthorInGrupo && (
          <div className="flex items-center gap-1.5 mb-0.5">
            <Avatar className="h-5 w-5">
              <AvatarImage src={sender?.foto_url || undefined} />
              <AvatarFallback className="text-[9px]">{getSenderInitials(sender?.nome)}</AvatarFallback>
            </Avatar>
            <span className={cn('text-[11px] font-semibold truncate max-w-[180px]', autoresGrupoWhats ? 'text-foreground' : senderColor)}>
              {sender?.nome || 'Membro'}
            </span>
            {equipeAutor?.equipe_nome && (
              <span
                className="text-[10.5px] truncate dark:brightness-200"
                style={{ color: equipeAutor.equipe_cor || undefined }}
              >
                · {equipeAutor.equipe_nome.charAt(0) + equipeAutor.equipe_nome.slice(1).toLowerCase()}
              </span>
            )}
          </div>
        )}
        {showAutorExterno && (
          <div className="flex items-center gap-1.5 mb-0.5">
            <Avatar className="h-5 w-5">
              <AvatarFallback className="text-[9px]">{getSenderInitials(autorExterno!.nome)}</AvatarFallback>
            </Avatar>
            <span className="text-[11px] font-semibold text-foreground truncate max-w-[180px]">{autorExterno!.nome}</span>
            <span className="text-[10.5px] text-muted-foreground">· {autorExterno!.telefone}</span>
          </div>
        )}
        {/* Citação (responder) — mini-bloco da mensagem original, estilo WhatsApp */}
        {grupo?.quoted && !isDeleted && (
          <div className={cn(
            'mb-1 rounded-md border-l-[3px] px-2 py-1 text-[12px] leading-tight',
            isOut ? 'border-primary/60 bg-primary/[0.04]' : 'border-primary/70 bg-muted/60',
          )}>
            <div className="font-semibold text-[11px] text-primary truncate">{grupo.quoted.autor}</div>
            <div className="text-muted-foreground truncate">{grupo.quoted.snippet || '📎 anexo'}</div>
          </div>
        )}
        {/* Etiqueta automático — agente IA recebe destaque vermelho/negrito + robô.
            Cores semanticas (text-danger, text-muted-foreground) funcionam em
            AMBAS as bolhas (off-white inbound e verde WhatsApp outbound) — nao
            precisa mais override por isOut. */}
        {showAutoLabel && msg.message_source === 'agente_ia' ? (
          <div className="mb-0.5 flex items-center gap-1">
            <span
              className={`inline-flex items-center justify-center h-4 w-4 rounded-md ${getAgenteCores(agenteIaNome).iconBg} text-white shadow-sm`}
              title="Resposta do Agente IA"
            >
              <BotMessageSquare className="h-2.5 w-2.5" strokeWidth={2.5} />
            </span>
            <span className={`font-mono text-[10px] font-bold tracking-tight ${getAgenteCores(agenteIaNome).nameGrad} bg-clip-text text-transparent`}>
              {getAgenteNomeCurto(agenteIaNome)}
            </span>
            <span className={`text-[10px] font-semibold ${getAgenteCores(agenteIaNome).labelText}`}>
              · Agente IA
            </span>
            <span className="text-[10px] text-muted-foreground/80">
              ({getAgenteEspecialidade(agenteIaNome)})
            </span>
          </div>
        ) : showAutoLabel && (
          <div className="mb-0.5">
            {(() => {
              const labelByMap: Record<string, { text: string; title: string }> = {
                aparelho: {
                  text: '📱 Enviado pelo aparelho',
                  title:
                    'Mensagem enviada pelo celular físico conectado a esta conexão, fora do CRM. Foi alguém digitando no WhatsApp do aparelho.',
                },
                tabulacao: {
                  text: '⚙ Tabulação automática',
                  title: 'Resposta automática gerada por regra de tabulação.',
                },
                apresentacao_lead: {
                  text: '👋 Apresentação Lead',
                  title: 'Mensagem de apresentação enviada no fluxo de proposta do lead (não é tabulação de discadora).',
                },
                apresentacao_carteira_lead: {
                  text: '👋 Apresentação Carteira',
                  title: 'Mensagem de apresentação de carteira enviada no fluxo de proposta do lead (não é tabulação de discadora).',
                },
                proposta_lead: {
                  text: '📄 Proposta Lead',
                  title: 'Proposta enviada no fluxo comercial do lead (não é tabulação de discadora).',
                },
                retorno_lead: {
                  text: '🔁 Retorno Lead',
                  title: 'Retorno/follow-up enviado no fluxo de proposta do lead (não é tabulação de discadora).',
                },
                fechamento_lead: {
                  text: '🤝 Fechamento Proposta Lead',
                  title: 'Mensagem de fechamento enviada ao concluir a proposta do lead (não é tabulação de discadora).',
                },
                qualidade: {
                  text: '⚙ Mensagem de qualidade',
                  title: 'Mensagem padronizada de qualidade.',
                },
                cliente: {
                  text: '⚙ Cliente',
                  title: 'Origem cliente.',
                },
                konnect_parceiro: {
                  text: 'Konnect',
                  title: 'A Konnect escreveu pela página dela (link da ficha no grupo), pelo nosso número. A Conecta fica pausada até 30 min depois da última mensagem.',
                },
                bee360: {
                  text: 'Enviado fora do CRM',
                  title: 'A linha oficial registrou esta mensagem, mas o CRM não tem quem a enviou: veio do aparelho da linha ou de antes da correção de 30/09, quando o registro do CRM se perdia.',
                },
              };
              const fallback = {
                text: `⚙ Automático · ${msg.message_source}`,
                title: 'Mensagem não enviada manualmente pelo CRM.',
              };
              const info = labelByMap[msg.message_source as string] || fallback;
              const ehAparelho = msg.message_source === 'aparelho';
              return (
                <span
                  className={ehAparelho
                    ? 'inline-block text-[10px] font-semibold uppercase tracking-wider text-primary dark:text-primary bg-muted dark:bg-muted/40 border border-border dark:border-primary rounded px-1.5 py-0.5'
                    : 'text-[10px] font-medium uppercase tracking-wider text-muted-foreground/80'}
                  title={info.title}
                >
                  {info.text}
                </span>
              );
            })()}
          </div>
        )}

        {/* Conteúdo */}
        {isDeleted ? (
          <p className="text-[13px] italic text-muted-foreground/70 flex items-center gap-1.5">
            <Trash2 className="h-3 w-3" /> Mensagem apagada
          </p>
        ) : isEditing && isTextLike ? (
          // Modo edicao inline — Textarea + botoes Salvar/Cancelar.
          // Enter salva, Esc cancela, Shift+Enter quebra linha (igual chat).
          <div className="flex flex-col gap-1.5 min-w-[240px]">
            <Textarea
              autoFocus
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSaveEdit();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setIsEditing(false);
                  setEditText(msg.body || '');
                }
              }}
              disabled={isSavingEdit}
              className="text-[12.5px] leading-[1.5] min-h-[60px] bg-background/60"
              rows={Math.min(8, Math.max(2, (editText.match(/\n/g)?.length || 0) + 2))}
            />
            {/* Aviso: fora da janela do WhatsApp -> cliente nao verá a edicao */}
            {!isNote && !dentroJanela && (
              <div className="flex items-start gap-1.5 rounded-md bg-primary/5 dark:bg-primary/40 border border-primary/40 dark:border-primary/60 px-2 py-1.5 text-[11px] text-primary dark:text-primary leading-tight">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Passou de 15min do envio.</strong> WhatsApp nao permite mais alterar no aparelho do cliente — ele continua vendo a versao original. Vai salvar so no CRM (pra historico).
                </span>
              </div>
            )}
            <div className="flex items-center justify-end gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-[11px]"
                onClick={() => { setIsEditing(false); setEditText(msg.body || ''); }}
                disabled={isSavingEdit}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                className="h-7 px-2 text-[11px]"
                onClick={handleSaveEdit}
                disabled={isSavingEdit}
              >
                {isSavingEdit ? <Loader2 className="h-3 w-3 animate-spin" /> : (!isNote && !dentroJanela ? 'Salvar no CRM' : 'Salvar')}
              </Button>
            </div>
          </div>
        ) : msg.type === 'text' || !msg.type ? (
          <p className="text-[12.5px] leading-[1.5] whitespace-pre-wrap break-words">
            {isGrupo ? renderTextoComMencoes(stripFaturaTags(msg.body)) : stripFaturaTags(msg.body)}
          </p>
        ) : msg.type === 'template' ? (
          // DEPRECATED 24/05/2026: removida marca "📢 Enviado por campanha".
          // Conversa nao distingue mais origem.
          <p className="text-[12.5px] leading-[1.5] whitespace-pre-wrap break-words">{stripFaturaTags(msg.body) || '[Template]'}</p>
        ) : msg.type === 'sticker' ? (
          // Figurinha (webp) — o GO entrega e a mídia é salva; sem este caso o
          // front mostrava só "[sticker]" e parecia mensagem perdida.
          msg.media_url ? (
            <img src={msg.media_url} alt="Figurinha" className="w-32 h-32 object-contain" loading="lazy" />
          ) : (
            <p className="text-sm text-muted-foreground">🎭 Figurinha</p>
          )
        ) : msg.type === 'image' ? (
          msg.media_url ? (
            <ImageBubble url={msg.media_url} isOut={isOut} caption={stripFaturaTags(msg.body)} onPreview={onPreview}
              onVincularChip={msg.direction === 'inbound' && !isNote && !isDeleted && !isGrupo ? () => {
                window.dispatchEvent(new CustomEvent('crm:vincular-chip', { detail: { conversaId: msg.conversation_id, mensagemId: msg.id } }));
              } : undefined} />
          ) : (
            <p className="text-sm text-muted-foreground">📷 Imagem</p>
          )
        ) : msg.type === 'document' ? (
          // WhatsApp original mostra anexo + caption no MESMO balão.
          // Aqui faz igual: bloco do PDF clicável + texto completo embaixo
          // (princípio Douglas 24/05: "o que envia é o que aparece, não
          // mostre msg resumida"). Antes mostrava só ícone + body truncado
          // em 200px — texto da fatura sumia.
          (() => {
            const captionFull = stripFaturaTags(msg.body);
            // Filename mais legível pra preview do anexo. Pega do URL ou
            // mostra rótulo neutro.
            const filenameGuess = (() => {
              const url = msg.media_url || '';
              const fromUrl = url.split('?')[0].split('/').pop();
              if (fromUrl && fromUrl.length > 4) return decodeURIComponent(fromUrl);
              return 'Documento PDF';
            })();
            return (
              <div className="flex flex-col gap-1.5">
                <div
                  className="flex items-center gap-2 cursor-pointer rounded-md px-2 py-1.5 bg-background/40 hover:bg-background/70 transition-colors border border-border/40"
                  onClick={() => msg.media_url && window.open(msg.media_url, '_blank')}
                >
                  <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-xs underline truncate flex-1 min-w-0">
                    {filenameGuess}
                  </span>
                </div>
                {captionFull && (
                  <p className="text-[12.5px] leading-[1.5] whitespace-pre-wrap break-words">
                    {captionFull}
                  </p>
                )}
              </div>
            );
          })()
        ) : msg.type === 'audio' ? (
          msg.media_url ? (
            <div className="w-[360px] max-w-full space-y-1.5">
              <AudioPlayer src={msg.media_url} className="max-w-full bg-card/80 shadow-none" />
              {msg.transcricao && (
                <div className="max-h-[190px] overflow-y-auto border-l-2 border-primary/25 pl-2 pr-1">
                  <span className="text-[9.5px] font-semibold uppercase tracking-wider text-primary/75">
                    Transcrição
                  </span>
                  <p className="whitespace-pre-wrap break-words text-[11.5px] leading-[1.45] text-muted-foreground">
                    {msg.transcricao}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Mic className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Áudio indisponível</span>
            </div>
          )
        ) : msg.type === 'video' ? (
          msg.media_url ? (
            <video controls className="rounded-xl max-h-52 max-w-[240px]" preload="metadata">
              <source src={msg.media_url} type={msg.mime_type || 'video/mp4'} />
            </video>
          ) : (
            <p className="text-sm text-muted-foreground">🎥 Vídeo indisponível</p>
          )
        ) : msg.type === 'button' || msg.type === 'interactive' ? (
          <div className="flex items-center gap-1.5">
            <span className="text-sm">💬</span>
            <p className="text-sm font-medium">
              {msg.body
                || (msg as any).raw_payload?.button?.text
                || (msg as any).raw_payload?.interactive?.button_reply?.title
                || 'Resposta interativa'}
            </p>
          </div>
        ) : msg.type === 'reaction' || (msg.body || '').trim() === '😊 Reação' ? (
          // Cliente reagiu a uma msg nossa com um emoji (ex: 👍). Renderiza
          // mais discreto e com o emoji do cliente em destaque.
          <p className="text-sm text-muted-foreground italic">
            Reagiu <span className="text-base not-italic">{(msg.body || '🙂').replace(/\s*Reação$/, '')}</span>
          </p>
        ) : msg.type === 'system' ? (
          // Mensagem de sistema do WhatsApp (msg revogada, chave alterada,
          // visualização única). Sem corpo útil pro operador.
          <p className="text-sm text-muted-foreground italic">⚙️ Mensagem do sistema</p>
        ) : msg.type === 'contacts' ? (
          // Cartão de contato (o Contato oficial Claro sai assim; o que o cliente
          // manda chega com o nome). Mostra quem é, como o WhatsApp mostra.
          <div className="flex items-center gap-2 min-w-[180px]">
            <UserPlus className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <p className="text-[12.5px] leading-[1.5] break-words">{msg.body || 'Contato'}</p>
          </div>
        ) : (
          // Fallback: prefere o body (que a trigger DB popula com labels úteis
          // tipo "😊 Reação", "📊 Enquete", "📍 Localização ao vivo" etc).
          // Só cai em [type] se realmente não houver body algum.
          <p className="text-sm text-muted-foreground italic">
            {msg.body || `[${msg.type}]`}
          </p>
        )}

        {isNote && !isGrupo && msg.conversation_id && (
          <FinalizarTarefaNaNota mensagemId={msg.id} conversaId={msg.conversation_id} corpo={msg.body} />
        )}

        {/* Rodapé da bolha */}
        <div className={cn('flex items-center gap-1 mt-1', isOut ? 'justify-end' : 'justify-start')}>
          {editedAt && !isEditing && (() => {
            // edit_escopo='so_crm' -> WhatsApp recusou, cliente ainda ve a original.
            // Visual diferenciado pra ficar obvio que NAO foi propagado pro cliente.
            const escopo = (msg as any).edit_escopo as string | null | undefined;
            const soNoCrm = escopo === 'so_crm';
            const original = (msg as any).original_body;
            const tooltip = `Editada em ${new Date(editedAt).toLocaleString('pt-BR')}${original ? `\n\nOriginal: ${original}` : ''}${soNoCrm ? '\n\n⚠ Cliente continua vendo a versao original (passou de 15min do WhatsApp).' : ''}`;
            return (
              <span
                className={cn(
                  'text-[10px] italic',
                  soNoCrm ? 'text-primary font-medium' : 'text-muted-foreground/60',
                )}
                title={tooltip}
              >
                {soNoCrm ? 'editada (so CRM)' : 'editada'}
              </span>
            );
          })()}
          <span className="text-[9.5px] tabular-nums text-muted-foreground/70">{formatMsgTime(msg.created_at)}</span>
          {canalSelo && !isNote && (
            <span
              className={cn('text-[10px] leading-none', canalSelo.cor)}
              title={canalSelo.title}
            >
              · {canalSelo.texto}
            </span>
          )}
          {/* Nota interna NAO tem icone de status (✓/✓✓): nao foi pro cliente,
              entao mostrar "delivered" confunde - parece que cliente viu. */}
          {isOut && !isNote && !isDeleted && (
            isGrupo ? (
              grupo?.lidoPorTodos
                ? <CheckCheck className="h-3.5 w-3.5 text-primary" aria-label="Lida por todos" />
                : <Check className="h-3.5 w-3.5 text-muted-foreground/70" aria-label="Enviada" />
            ) : (
              <StatusIcon status={msg.status} />
            )
          )}
          {msg.status === 'failed' && (
            <span
              className="text-[10px] text-danger/70"
              title={msg.erro_detalhe || undefined}
            >
              Falhou: {describeWhatsAppError(msg.erro_codigo, msg.erro_detalhe)}
              {codigoExibidoWhatsApp(msg.erro_codigo, msg.erro_detalhe)
                ? ` (${codigoExibidoWhatsApp(msg.erro_codigo, msg.erro_detalhe)})`
                : ''}
            </span>
          )}
        </div>
        </div>
        {/* Reações — chips embaixo da bolha, estilo WhatsApp */}
        {grupo && !isDeleted && (grupo.reacoes?.length || 0) > 0 && (() => {
          const porEmoji = new Map<string, { qtd: number; nomes: string[]; minha: boolean }>();
          for (const r of grupo.reacoes!) {
            const e = porEmoji.get(r.emoji) || { qtd: 0, nomes: [], minha: false };
            e.qtd++;
            if (r.usuario?.nome) e.nomes.push(r.usuario.nome);
            if (r.usuario_id === meuUserId) e.minha = true;
            porEmoji.set(r.emoji, e);
          }
          return (
            <div className={cn('flex flex-wrap gap-1 -mt-1 relative z-[1]', isOut ? 'justify-end pr-1.5' : 'pl-1.5')}>
              {[...porEmoji.entries()].map(([emoji, info]) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => grupo.onReact?.(emoji)}
                  title={info.nomes.join(', ')}
                  className={cn(
                    'rounded-full bg-card border border-border shadow-sm px-1.5 py-0.5 text-[12px] leading-none flex items-center gap-1 hover:bg-accent transition-colors',
                    info.minha && 'border-primary ring-1 ring-primary/40',
                  )}
                >
                  <span>{emoji}</span>
                  {info.qtd > 1 && <span className="text-[10px] text-muted-foreground tabular-nums">{info.qtd}</span>}
                </button>
              ))}
            </div>
          );
        })()}
        {/* Ações no hover — grupo: responder / reagir / editar / apagar.
            Fora de grupo: só o lápis de editar (comportamento original). */}
        {grupo && !isEditing && !isDeleted && (
          <div
            className={cn(
              'absolute top-0 opacity-0 group-hover:opacity-100 transition-opacity',
              'flex items-center gap-0.5 bg-background/95 border border-border shadow-sm rounded-full px-1 py-0.5 z-[2]',
              isOut ? 'right-full mr-1.5' : 'left-full ml-1.5',
            )}
          >
            <button
              type="button"
              onClick={() => grupo.onReply?.()}
              className="h-6 w-6 rounded-full flex items-center justify-center hover:bg-accent"
              title="Responder"
            >
              <CornerUpLeft className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            <Popover open={reactOpen} onOpenChange={setReactOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="h-6 w-6 rounded-full flex items-center justify-center hover:bg-accent"
                  title="Reagir"
                >
                  <SmilePlus className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent side="top" className="w-auto p-1.5 flex gap-1">
                {['👍', '❤️', '😂', '😮', '😢', '🙏'].map((e) => (
                  <button
                    key={e}
                    type="button"
                    className="text-[20px] leading-none hover:scale-125 transition-transform p-0.5"
                    onClick={() => { grupo.onReact?.(e); setReactOpen(false); }}
                  >
                    {e}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
            {canEdit && (
              <button
                type="button"
                onClick={() => { setEditText(msg.body || ''); setIsEditing(true); }}
                className="h-6 w-6 rounded-full flex items-center justify-center hover:bg-accent"
                title="Editar mensagem"
              >
                <Pencil className="h-3 w-3 text-muted-foreground" />
              </button>
            )}
            {grupo.podeApagar && (
              <button
                type="button"
                onClick={() => { if (window.confirm('Apagar esta mensagem pra todos do grupo?')) grupo.onDelete?.(); }}
                className="h-6 w-6 rounded-full flex items-center justify-center hover:bg-danger/10"
                title="Apagar mensagem"
              >
                <Trash2 className="h-3 w-3 text-danger" />
              </button>
            )}
          </div>
        )}
        {!grupo && canEdit && !isEditing && (
          <button
            type="button"
            onClick={() => { setEditText(msg.body || ''); setIsEditing(true); }}
            className={cn(
              'absolute top-1 opacity-0 group-hover:opacity-100 transition-opacity',
              'h-6 w-6 rounded-full bg-background/90 border border-border shadow-sm',
              'flex items-center justify-center hover:bg-background',
              isOut ? '-left-7' : '-right-7',
            )}
            title="Editar mensagem"
          >
            <Pencil className="h-3 w-3 text-muted-foreground" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Chip discreto exibido ao lado do nome da conexão quando há falhas
 * recentes em códigos de nível-conexão (131031 / 368 / 133000).
 *
 * Visual sutil: tons âmbar (não destructive), tamanho pequeno, integrado
 * ao header. Click abre popover com nome, telefone formatado, descrição
 * do erro e botão "Copiar pra chamado" — pra agilizar abertura de ticket
 * com a 360dialog.
 */
function ConnectionHealthChip({
  nome,
  telefone,
  conversationId,
  health,
}: {
  nome: string | null | undefined;
  telefone: string | null | undefined;
  conversationId: string;
  health: {
    severity: 'restricted_temp' | 'likely_banned' | 'ok';
    codigoPredominante: string | null;
    totalFalhas: number;
    primeiraFalhaEm: string | null;
    windowHours: number;
  };
}) {
  const label = severityLabel(health.severity);
  if (!label) return null;

  const isSevere = health.severity === 'likely_banned';
  const telFormatted = formatConnectionPhone(telefone);

  const handleCopy = async () => {
    const txt = buildIncidentText({
      connectionNome: nome,
      connectionTelefone: telefone,
      code: health.codigoPredominante,
      conversationId,
      primeiraFalhaEm: health.primeiraFalhaEm,
      totalFalhas: health.totalFalhas,
      windowHours: health.windowHours,
    });
    try {
      await navigator.clipboard.writeText(txt);
      toast.success('Copiado — cole no chamado da 360dialog');
    } catch {
      toast.error('Não consegui copiar. Selecione manualmente.');
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'ml-1 inline-flex items-center gap-1 rounded px-1.5 py-0 text-[10px] leading-4',
            'border transition-colors',
            isSevere
              ? 'text-danger bg-danger/10 hover:bg-danger/15 border-danger/40'
              : 'text-primary bg-primary/10 hover:bg-primary/15 border-primary/40',
          )}
          aria-label={`${label} — clique para detalhes`}
        >
          <CircleDot className="h-2.5 w-2.5" />
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3 text-xs">
        <div className="space-y-1.5">
          <div className="font-medium text-sm">{nome || 'Conexão'}</div>
          {telFormatted && (
            <div className="text-muted-foreground tabular-nums">{telFormatted}</div>
          )}
          <div className="pt-1">
            {describeWhatsAppError(health.codigoPredominante, null)}
            {health.codigoPredominante && (
              <span className="text-muted-foreground"> · código {health.codigoPredominante}</span>
            )}
          </div>
          <div className="text-muted-foreground">
            {health.totalFalhas} {health.totalFalhas === 1 ? 'falha' : 'falhas'} nas últimas {health.windowHours}h
          </div>
          <div className="pt-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs gap-1.5"
              onClick={handleCopy}
            >
              <Copy className="h-3 w-3" />
              Copiar info pra chamado
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface ChatWindowProps {
  conversation: Conversation | null;
  onBack?: () => void;
  /** No celular o Painel CRM não cabe ao lado: este abre o painel em tela cheia. */
  onAbrirPainel?: () => void;
  onConversationCleared?: () => void;
  onConversationReopened?: () => void;
  /** (§6 punch 24d) chamado após o SUCESSO do envio de mensagem ao cliente —
   * o painel da triagem usa pra mostrar a barra "isso resolve o caso?". */
  onMessageSent?: () => void;
  onConversationChanged?: () => void;
  /** (§6 punch 24d) faixa "VOCÊ ESTÁ RESOLVENDO" — renderiza logo abaixo do
   * header, acima das mensagens. */
  headerBanner?: React.ReactNode;
  /** (§6 punch 24d) barra de fechamento — renderiza acima do composer,
   * full-bleed, mesma âncora do preview de imagem colada. */
  composerBanner?: React.ReactNode;
}

export default function ChatWindow({ conversation, onBack, onAbrirPainel, onConversationCleared, onConversationReopened, onMessageSent, onConversationChanged, headerBanner, composerBanner }: ChatWindowProps) {
  const { profile } = useAuth();
  const [messageParams, setMessageParams] = useSearchParams();
  const messageLocation = useLocation();
  const targetMessageId = messageParams.get('conversation') === conversation?.id ? messageParams.get('message') : null;
  const { messages, isLoading, hasMore, loadMore, targetError, sincronizar: sincronizarMensagens } = useMessages(conversation?.id || null, targetMessageId, targetMessageId ? messageLocation.key : undefined);
  const senders = useMessageSenders(messages.map((m) => m.sent_by_user_id));
  // Uso fora do inbox: mensagens enviadas pelo aparelho/WhatsApp Web (fora do CRM)
  // nas ultimas 24h desta conversa. Sinaliza risco de desconexao/ban.
  const aparelhoRecente = messages.filter(
    (m) => m.message_source === 'aparelho' && (Date.now() - new Date(m.created_at).getTime()) < 86400000,
  ).length;
  const { data: connHealth } = useConnectionHealth(conversation?.connection_id || null);
  const { data: campanhasIaOnly } = useCampanhasIaOnly();
  // Saúde da instância Evolution (só ativa quando provider=evolution).
  // Quando isDown=true, substituímos o composer por <EvolutionDownBanner />.
  const evolutionHealth = useEvolutionHealth(conversation?.connection_id || null);
  // FONTE UNICA de saude (whatsapp_connection_health). statusDe()==='fora' (critical)
  // significa sessao fantasma: Evolution diz 'open' mas a sessao morreu, entao o
  // isDown cru nao dispara. Combinamos com a saude real pra o banner aparecer mesmo assim.
  const { statusDe, saudeDe } = useSaudeConexoes();
  // O DONO DO CLIENTE (Douglas 29/09/2026): "o cliente não perde o dono". Quem
  // atende é owner_user_id; o dono mora em consultor_comercial_id. Sem ninguém
  // atendendo, a faixa diz de quem é o cliente em vez de "Sem Dono".
  const { data: donoDoCliente } = useQuery({
    queryKey: ['conversa-dono-do-cliente', conversation?.id],
    enabled: !!conversation?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<{ id: string; nome: string } | null> => {
      const { data: c } = await supabase.from('whatsapp_conversations')
        .select('consultor_comercial_id').eq('id', String(conversation?.id)).maybeSingle();
      const id = c?.consultor_comercial_id;
      if (!id) return null;
      const { data: u } = await supabase.from('usuarios').select('id, nome').eq('id', id).maybeSingle();
      return u ? { id: u.id, nome: u.nome } : null;
    },
  });
  const saudeForaReal = statusDe(conversation?.connection_id || null) === 'fora';
  // So mostra o banner de queda quando ha instanceName (conexao Evolution) — saudeForaReal
  // ja cobre apenas Evolution (a tabela de saude so popula essas conexoes).
  const mostrarBannerCaida =
    (evolutionHealth.isDown || evolutionHealth.isLooping || saudeForaReal) && !!evolutionHealth.instanceName;
  // FAILOVER (08/07): linha da conversa caida NAO bloqueia mais a digitacao quando o
  // usuario tem OUTRA linha saudavel — o backend desvia o envio pela cadeia
  // (primaria -> secundaria -> reserva) na hora do envio. Bloqueio total so quando
  // nao ha saida nenhuma, ou em GRUPO (grupo vive numa linha especifica, nunca troca).
  const minhaSaude = useMinhaConexaoSaude(profile?.id);
  // Outra linha SUA no ar, na ordem secundaria -> reserva (nunca a que caiu, nunca grupo).
  const linhaSaidaFailover = (() => {
    const outras = minhaSaude.conexoes.filter((c) => c.status === 'conectada' && c.id !== conversation?.connection_id);
    const ordem = (p?: string | null) => (p === 'secundaria' ? 0 : p === 'reserva' ? 1 : 2);
    return [...outras].sort((a, b) => ordem((a as any).papel) - ordem((b as any).papel))[0] || null;
  })();
  const ehGrupoParaBanner = !!(conversation as any)?.chat_grupo_id;
  // Linha caida SEMPRE bloqueia a digitacao (nunca "manda pela outra fingindo"). Se ha outra
  // linha SUA no ar, o rodape mostra um botao pra abrir la, com msg pronta. (Douglas 10/07)
  const bannerBloqueia = mostrarBannerCaida && (!linhaSaidaFailover || ehGrupoParaBanner);
  const temSaidaOutraLinha = mostrarBannerCaida && !bannerBloqueia;
  // Janela de 24h da LINHA OFICIAL fechada: texto livre é aceito e DESCARTADO
  // MUDO pela Meta (lei 11/08) — o consultor "enviava" e nada chegava. Nesse
  // estado o composer vira o seletor de TEMPLATE APROVADO (que reabre a
  // conversa de verdade). getWindowExpiresAt devolve null para linha
  // não-oficial, então isto só age em conversa bee360/360dialog.
  /* A ETAPA DO PEDIDO DESTA CONVERSA — a fila e o que ela significa.
     Duas fontes que já existem, nenhuma consulta nova por conversa: o cartão
     (mesma view da lista) diz em que fila o cliente está; o dicionário diz o
     que aquela fila prova e o que é proibido dizer nela. */
  const { data: cartaoConversa } = useConversaCartao(conversation?.id ? [conversation.id] : []);
  const { data: dicionarioSituacao } = useDicionarioSituacao();
  const etapaDaConversa = (() => {
    const c = conversation?.id ? cartaoConversa?.get(conversation.id) : null;
    if (!c?.coluna_atual) return null;
    const sit = dicionarioSituacao?.[chaveSituacao(c.quadro_atual, c.coluna_atual)];
    return {
      fila: rotuloColuna(c.coluna_atual),
      prova: sit?.prova ?? null,
      nao_diga: sit?.nao_diga ?? null,
    };
  })();

  /* Precisa de cadastro = sem documento E sem lead. O LEAD entra na conta: são
     226 conversas sem documento mas COM lead, que já têm cliente identificado e
     não devem pedir nada. Mesma régua da lei e da aba da lista. */
  const precisaCadastro = !!conversation
    && !(conversation as { chat_grupo_id?: string | null }).chat_grupo_id
    && String(conversation.cliente_cnpj || '').replace(/\D/g, '') === ''
    && !conversation.lead_id;
  const [cadastroAberto, setCadastroAberto] = useState(false);

  const janelaOficialExpira = getWindowExpiresAt(conversation);
  const foraDaJanelaOficial = !!conversation && !conversation.chat_grupo_id
    && janelaOficialExpira !== null && janelaOficialExpira <= Date.now();
  // Continuidade no lugar CERTO: abre (ou cria) a conversa deste cliente na linha
  // saudavel — e' la que as RESPOSTAS dele vao chegar quando o envio sai por failover.
  const continuarPelaLinhaSaudavel = async () => {
    if (!linhaSaidaFailover || !conversation) return;
    const tel = (conversation as any)?.telefone_canonico || conversation?.telefone;
    if (!tel) { toast.error('Conversa sem telefone — não dá pra abrir na outra linha.'); return; }
    const { data: convId, error } = await supabase.rpc('whatsapp_find_or_create_conversation', {
      p_telefone: String(tel),
      p_connection_id: linhaSaidaFailover.id,
      p_owner_user_id: conversation?.owner_user_id || profile?.id || null,
    });
    if (error || !convId) { toast.error('Não foi possível abrir a conversa na outra linha.'); return; }
    // Msg pronta e transparente: o cliente recebe do outro numero SABENDO o motivo.
    const primeiroNome = String((conversation as any)?.contato_nome || (conversation as any)?.nome || '').trim().split(/\s+/)[0] || '';
    const msgPronta = `Olá ${primeiroNome ? primeiroNome + ', ' : ''}estou te chamando pela minha outra linha porque a minha está com problemas técnicos. Podemos seguir por aqui?`;
    try { localStorage.setItem(`wa-draft-${convId}`, msgPronta); } catch { /* draft best-effort */ }
    window.open(`/whatsapp-inbox?conversation=${convId}`, '_blank');
  };

  // 🆕 "Quem está atendendo AGORA": linha 24h (agente_ia_ativo + sem dono) OU
  // a IsabellA respondeu por último nesta conversa (agente_ia_atendendo — vale
  // pras linhas de consultor cobertas à noite, mesmo com dono estrutural).
  // A flag é mantida pelo trigger: zera assim que um humano responde.
  // ⛔ Nota interna mandou a IA parar (Douglas 21/09/2026): enquanto a pausa
  // existe ela nao esta atendendo, e o rodape nao pode dizer que esta.
  const iaPausadaPorNota = !!conversation?.ia_pausada_em;
  const iaAtendendoAgora =
    !iaPausadaPorNota && (
      (!!conversation?.agente_ia_ativo && !conversation?.owner_user_id) ||
      !!conversation?.agente_ia_atendendo);

  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);
  // Mensagem escrita que nao saiu porque o BACKEND estava fora (deploy, restart).
  // Nada foi gravado no banco nesse caso, entao reenviar sozinho nao duplica.
  // Sem isso o texto morria: o campo e' limpo no inicio do envio e o catch nao
  // devolvia nada. (Douglas 22/09/2026: "guardar e reenviar sozinho")
  const [reenvioPendente, setReenvioPendente] = useState<{ corpo: string; convId: string } | null>(null);
  const [isClosingEmpty, setIsClosingEmpty] = useState(false);
  // Modo "nota interna" — quando true, a próxima mensagem enviada é gravada como
  // nota visível APENAS no CRM (is_private_note=true) e NÃO é enviada ao cliente
  // pelo WhatsApp. Usado tanto pela equipe (anotações de atendimento) quanto pela
  // automação do agente IA (gatilho [FECHAMENTO_VENDA]).
  const [isInternalMode, setIsInternalMode] = useState(false);
  // Seletor de TEMPLATE aprovado sob demanda (Douglas 13/08): além do automático
  // com janela fechada, o consultor pode abrir o seletor a qualquer momento numa
  // conversa de linha oficial (botão 📋 no composer).
  // Mini-composer de nota interna no banner "está atendendo" — permite
  // escrever nota sem precisar assumir o atendimento (Douglas 26/05).
  const [bannerNoteOpen, setBannerNoteOpen] = useState(false);
  const [bannerNoteText, setBannerNoteText] = useState('');
  const [bannerNoteSending, setBannerNoteSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState<{url: string; name: string} | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [catalogoOpen, setCatalogoOpen] = useState(false);
  // Mensagem pronta vinda do botao "falar pela minha outra linha": pre-preenche o composer
  // ao abrir a conversa nova na linha saudavel (localStorage p/ cruzar a aba). (Douglas 10/07)
  useEffect(() => {
    if (!conversation?.id) return;
    try {
      const d = localStorage.getItem(`wa-draft-${conversation.id}`);
      if (d) { setText(d); localStorage.removeItem(`wa-draft-${conversation.id}`); }
    } catch { /* draft best-effort */ }
  }, [conversation?.id]);

  const [recordingDuration, setRecordingDuration] = useState(0);
  const oggRecorderRef = useRef<Recorder | null>(null);
  const oggFilePromiseRef = useRef<Promise<File | null> | null>(null);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const targetElementRef = useRef<HTMLDivElement>(null);
  const targetLoaded = !!targetMessageId && messages.some(m => m.id === targetMessageId);
  useEffect(() => {
    if (!targetLoaded || isLoading) return;
    const container = scrollRef.current;
    const target = targetElementRef.current;
    if (!container || !target) return;
    ancoraTopo.current = null;
    const position = () => {
      container.scrollTop += target.getBoundingClientRect().top - container.getBoundingClientRect().top - 24;
    };
    const raf = requestAnimationFrame(position);
    // Mídias anteriores podem mudar a altura depois do primeiro paint.
    const media = container.querySelectorAll('img, audio, video');
    media.forEach(el => { el.addEventListener('load', position); el.addEventListener('loadedmetadata', position); });
    const release = () => media.forEach(el => { el.removeEventListener('load', position); el.removeEventListener('loadedmetadata', position); });
    // Depois de uma interação manual, não disputar a rolagem com o consultor.
    container.addEventListener('wheel', release, { once: true });
    container.addEventListener('touchstart', release, { once: true });
    return () => {
      cancelAnimationFrame(raf);
      release();
      container.removeEventListener('wheel', release);
      container.removeEventListener('touchstart', release);
    };
  }, [targetLoaded, targetMessageId, isLoading, messageLocation.key]);

  // Tick de 1 min para re-renderizar o chip "Janela 24h" sem precisar de outro update
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  // ⛔ (Douglas 16/09/2026) "Quando clico em carregar mais, ele volta tudo até o
  // final e eu tenho que rolar tudo novamente até o alto."
  // O efeito abaixo rola para o fim sempre que `messages.length` muda — e carregar
  // mensagens ANTIGAS também muda o length. O leitor era jogado de volta ao pé da
  // conversa exatamente quando pediu para subir. Aqui guardamos a régua antes do
  // prepend; o efeito, vendo a marca, recoloca a vista no mesmo trecho em vez de
  // descer. Fica só o quanto a lista cresceu ACIMA, que é o que o navegador não
  // sabe compensar sozinho.
  const ancoraTopo = useRef<{ alturaAntes: number; topoAntes: number } | null>(null);
  const carregarAnteriores = useCallback(() => {
    const el = scrollRef.current;
    if (el) ancoraTopo.current = { alturaAntes: el.scrollHeight, topoAntes: el.scrollTop };
    loadMore();
  }, [loadMore]);

  // Scroll para última mensagem — robusto contra mídias que carregam tarde
  // (imagens/áudios mudam a altura do container depois da renderização inicial,
  // o que faz o scrollIntoView 'smooth' parar no meio da conversa).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Veio de "Carregar mensagens anteriores": mantém o trecho que a pessoa
    // estava lendo e não desce nada.
    const ancora = ancoraTopo.current;
    if (ancora) {
      ancoraTopo.current = null;
      const reposicionar = () => {
        el.scrollTop = el.scrollHeight - ancora.alturaAntes + ancora.topoAntes;
      };
      reposicionar();
      const rafAncora = requestAnimationFrame(reposicionar);
      return () => cancelAnimationFrame(rafAncora);
    }

    if (targetMessageId) return;

    // Scroll instantâneo ao trocar de conversa, suave em novas msgs
    const scrollToBottom = (smooth = false) => {
      el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    };

    // Primeiro tick: vai imediato pro fim
    scrollToBottom(false);

    // Re-scroll após o paint (caso layout ainda esteja se ajustando)
    const raf = requestAnimationFrame(() => scrollToBottom(false));

    // Re-scroll quando imagens/áudios/vídeos terminarem de carregar
    const mediaEls = el.querySelectorAll('img, audio, video');
    const handler = () => scrollToBottom(false);
    mediaEls.forEach((m) => {
      m.addEventListener('load', handler, { once: true });
      m.addEventListener('loadedmetadata', handler, { once: true });
    });

    return () => {
      cancelAnimationFrame(raf);
      mediaEls.forEach((m) => {
        m.removeEventListener('load', handler);
        m.removeEventListener('loadedmetadata', handler);
      });
    };
  }, [messages.length, conversation?.id, targetMessageId]);

  // NOVO MODELO (24/05/2026): IA atende primeiro. Conversa nova NAO é atribuida
  // automaticamente — fica sem dono (IA atende se conexao tem agente_ativo).
  // Pra escrever, consultor tem que clicar "Assumir Atendimento" explicitamente.
  // Antes tinha auto-claim aqui — removido porque virava "minha" so de clicar
  // pra olhar, mesmo quando IA tava atendendo. Veja handleAssumirAtendimento
  // logo abaixo.
  const [isAssumindo, setIsAssumindo] = useState(false);
  const queryClient = useQueryClient();
  // Grupos internos: qualquer membro ativo escreve (RLS valida no DB).
  // Banner "Assumir Atendimento" não faz sentido em grupo.
  // Defesa em profundidade: detecta via chat_grupo_id OU prefixo sintético
  // __GRUPO__ do telefone — caso o objeto venha de um path que não populou
  // o campo (handleNavigateToConversation antigamente não trazia).
  const isGrupoInterno = !!(conversation as any)?.chat_grupo_id ||
    !!conversation?.telefone?.startsWith('__GRUPO__');
  const permissaoEscrita = useWhatsAppWritePermission(conversation, isGrupoInterno);
  const podeEscrever = !!conversation && (
    isGrupoInterno || (permissaoEscrita.data?.enviar.permitido === true && !permissaoEscrita.isError
      && conversation.owner_user_id === profile?.id && !iaAtendendoAgora)
  );

  // ── Grupos internos v2: reações, leituras, typing, reply e menções ──
  const grupoIdAtivo = isGrupoInterno
    ? (((conversation as any)?.chat_grupo_id as string | null) ||
       (conversation?.telefone || '').replace('__GRUPO__', '') || null)
    : null;
  // Grupo do WhatsApp adotado pelo CRM: mesma tela do grupo interno, mas a
  // mensagem sai de verdade para o grupo pela Bee.
  const { data: gruposChat } = useChatGrupos();
  const ehGrupoWhats = !!grupoIdAtivo && !!gruposChat?.some((g) => g.id === grupoIdAtivo && g.tipo === 'whatsapp');
  const enviarNoGrupoWhats = async (messageId: string | undefined) => {
    if (!messageId) return;
    try {
      await postConversationAction('/v1/whatsapp/grupo-whatsapp/enviar', { message_id: messageId });
    } catch (e) {
      toast.error('Não saiu no grupo do WhatsApp: ' + (e instanceof Error ? e.message : String(e)));
    }
  };
  // Mensagem que chega do grupo traz o autor no texto ("**Nome**:\n(tel)").
  // Casado com o cadastro, vira autor de verdade: foto, nome e equipe.
  const { data: autoresGrupo } = useAutoresGrupoWhats(ehGrupoWhats);
  const mensagensTela = useMemo(() => {
    // ⛔ Evento do WhatsApp não é mensagem (Douglas 21/09/2026). protocolMessage
    // (apagar mensagem, mensagem temporária, sincronismo) virava um balão "⚙️
    // Sistema" sem conteúdo nenhum para quem lê a conversa — e a IsabellA chegou a
    // perguntar ao cliente se aquilo era "o protocolo do chamado técnico".
    // A linha continua no banco; ela só sai da tela.
    const semEventoDoWhats = messages.filter(
      (m) => m.type !== 'protocol' && (m.body || '').trim() !== '⚙️ Sistema',
    );
    if (!ehGrupoWhats) return semEventoDoWhats;
    return semEventoDoWhats.map((m) => {
      if (m.direction !== 'inbound') return m;
      const a = lerAutorGrupoWhats(m.body);
      if (!a) return m;
      const chave = chaveTelefoneGrupo(a.telefone);
      const u = chave ? autoresGrupo?.porTelefone.get(chave) : null;
      return {
        ...m,
        body: a.texto,
        sent_by_user_id: u?.id ?? null,
        autor_externo: u ? null : { nome: a.nome, telefone: a.telefone },
      };
    });
  }, [messages, ehGrupoWhats, autoresGrupo]);
  const sendersTela = useMemo(() => {
    if (!ehGrupoWhats || !autoresGrupo) return senders;
    const m = new Map(senders);
    autoresGrupo.porId.forEach((a, id) => m.set(id, { id, nome: a.nome, foto_url: a.foto_url }));
    return m;
  }, [senders, ehGrupoWhats, autoresGrupo]);
  // No grupo do WhatsApp foto e equipe do autor chegam depois das mensagens e
  // cada bolha cresce: sem descer de novo, a conversa abria no meio.
  useEffect(() => {
    if (!ehGrupoWhats || !autoresGrupo || targetMessageId) return;
    const el = scrollRef.current;
    if (!el) return;
    const descer = () => el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
    descer();
    const raf = requestAnimationFrame(descer);
    return () => cancelAnimationFrame(raf);
  }, [ehGrupoWhats, autoresGrupo, conversation?.id, targetMessageId]);
  const { data: reacoesMap } = useReacoesConversa(conversation?.id || null, !!conversation);
  const reagirMut = useReagirMensagem();
  const reagirRealMut = useReagirMensagemReal();
  const apagarMut = useApagarMensagem();
  const marcarLidoMut = useMarcarGrupoLido();
  const { data: leiturasGrupo } = useLeiturasGrupo(grupoIdAtivo);
  const { data: membrosGrupo } = useChatGrupoMembros(grupoIdAtivo);
  const { data: typingGrupo } = useTypingGrupo(grupoIdAtivo);
  const [replyTo, setReplyTo] = useState<(typeof messages)[0] | null>(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const mencoesRef = useRef<{ id: string; token: string }[]>([]);

  // Abriu o grupo / chegou mensagem com a conversa aberta → marca como lido
  useEffect(() => {
    if (grupoIdAtivo) marcarLidoMut.mutate(grupoIdAtivo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupoIdAtivo, messages.length]);

  // Troca de conversa limpa reply/menções
  useEffect(() => {
    setReplyTo(null);
    setMentionOpen(false);
    mencoesRef.current = [];
  }, [conversation?.id]);

  const msgById = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages]);
  const msgByProviderId = useMemo(() => {
    const map = new Map<string, (typeof messages)[0]>();
    for (const m of messages) if ((m as any).provider_message_id) map.set((m as any).provider_message_id, m);
    return map;
  }, [messages]);

  // Resolve a citação de uma mensagem: nossa (reply_to_message_id) ou do
  // cliente (contextInfo.stanzaId no raw_payload do webhook).
  const resolverQuoted = (msg: (typeof messages)[0]): { autor: string; snippet: string } | null => {
    const rid = (msg as any).reply_to_message_id as string | null;
    let alvo = rid ? msgById.get(rid) : undefined;
    if (!alvo) {
      const rp = (msg as any).raw_payload;
      const ctx = rp?.message?.extendedTextMessage?.contextInfo
        || rp?.message?.imageMessage?.contextInfo
        || rp?.message?.audioMessage?.contextInfo
        || rp?.message?.videoMessage?.contextInfo
        || rp?.message?.documentMessage?.contextInfo
        || rp?.contextInfo;
      const stanza = ctx?.stanzaId;
      if (stanza) alvo = msgByProviderId.get(stanza);
      if (!alvo && stanza) return { autor: 'Mensagem anterior', snippet: String(ctx?.quotedMessage?.conversation || '').slice(0, 120) };
    }
    if (!alvo) return null;
    const autor = alvo.sent_by_user_id === profile?.id
      ? 'Você'
      : alvo.direction === 'inbound'
        ? (conversation?.contact_name || conversation?.destinatario_nome || 'Cliente')
        : (alvo.sent_by_user_id ? (senders.get(alvo.sent_by_user_id)?.nome || 'Equipe') : 'Equipe');
    const snippet = (alvo.body && alvo.body.trim())
      ? alvo.body.slice(0, 120)
      : (alvo.type === 'image' ? '📷 Imagem' : alvo.type === 'audio' ? '🎤 Áudio' : alvo.type === 'video' ? '🎥 Vídeo' : alvo.type === 'document' ? '📄 Documento' : '📎 Anexo');
    return { autor, snippet };
  };

  const souAdminDoGrupo = useMemo(() => {
    if (!isGrupoInterno) return false;
    if (profile?.role === 'master') return true;
    return (membrosGrupo || []).some(
      (m) => m.usuario_id === profile?.id && m.ativo && m.papel === 'admin',
    );
  }, [isGrupoInterno, membrosGrupo, profile?.id, profile?.role]);

  // Momento até o qual TODOS os outros membros ativos leram (recibo ✓✓)
  const lidoPorTodosAte = useMemo(() => {
    if (!isGrupoInterno || !leiturasGrupo) return null;
    const outros = (membrosGrupo || []).filter((m) => m.ativo && m.usuario_id !== profile?.id);
    if (outros.length === 0) return null;
    const mapa = new Map(leiturasGrupo.map((l) => [l.usuario_id, l.last_read_at]));
    let min: string | null = null;
    for (const m of outros) {
      const lr = mapa.get(m.usuario_id);
      if (!lr) return null;
      if (!min || lr < min) min = lr;
    }
    return min;
  }, [isGrupoInterno, leiturasGrupo, membrosGrupo, profile?.id]);

  const membrosParaMencao = useMemo(() => {
    if (!mentionOpen) return [];
    const f = mentionFilter;
    return (membrosGrupo || [])
      .filter((m) => m.ativo && m.usuario_id !== profile?.id && !!m.usuario?.nome)
      .filter((m) => !f || (m.usuario!.nome || '').toLowerCase().includes(f))
      .slice(0, 6);
  }, [mentionOpen, mentionFilter, membrosGrupo, profile?.id]);

  const composerRef = useRef<HTMLTextAreaElement>(null);
  const corretor = useCorretorTexto(text, !isGrupoInterno);
  useLayoutEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 260)}px`;
  }, [text]);

  const handleComposerChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    setText(v);
    if (!isGrupoInterno && conversation?.id && podeEscrever && v.trim() && !isInternalMode) {
      emitirPresencaDigitando(conversation.id);
    }
    if (isGrupoInterno && grupoIdAtivo) {
      if (v.trim()) emitirTypingGrupo(grupoIdAtivo);
      const m = v.match(/(^|\s)@([\wÀ-ÿ]*)$/);
      if (m) {
        setMentionOpen(true);
        setMentionFilter((m[2] || '').toLowerCase());
      } else if (mentionOpen) {
        setMentionOpen(false);
      }
    }
  };

  const selecionarMencao = (usuarioId: string, nome: string) => {
    const token = '@' + primeiroNome(nome);
    setText((prev) => prev.replace(/(^|\s)@[\wÀ-ÿ]*$/, (mm) => {
      const sep = mm.startsWith(' ') ? ' ' : (mm.startsWith('\n') ? '\n' : '');
      return sep + token + ' ';
    }));
    mencoesRef.current = [
      ...mencoesRef.current.filter((t) => t.id !== usuarioId),
      { id: usuarioId, token },
    ];
    setMentionOpen(false);
  };

  async function handleAssumirAtendimento() {
    if (!conversation?.id || !profile?.id || isAssumindo) return;
    setIsAssumindo(true);
    try {
      // Ao assumir: vira dono + status='em_atendimento'. Assumir = "humano
      // vai cuidar agora, mas IA fica ouvindo de lado pra retomar quando puder".
      // A API valida o dono antes de assumir; o Inbox não transfere a posse comercial.
      await postConversationAction(`/v1/whatsapp/conversas/${conversation.id}/assumir`);
      queryClient.invalidateQueries({ queryKey: ['whatsapp-write-permission'] });

      // Optimistic update + invalidate: banner azul "X está atendendo" some
      // na hora, sem esperar realtime do Supabase (que pode demorar 1-3s ou
      // chegar com lag). Antes o consultor clicava Assumir e via a UI travada
      // até o cache invalidar sozinho — confundia.
      queryClient.setQueriesData<any>(
        { queryKey: ['whatsapp-conversations'] },
        (old: any) => {
          if (!old) return old;
          const patch = (c: any) =>
            c?.id === conversation.id
              ? { ...c, owner_user_id: profile.id, agente_ia_atendendo: false, status: 'em_atendimento' }
              : c;
          if (Array.isArray(old)) return old.map(patch);
          if (old?.pages) return { ...old, pages: old.pages.map((p: any[]) => p.map(patch)) };
          return old;
        },
      );
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });

      onConversationChanged?.();
      toast.success('Você assumiu o atendimento');
    } catch (e: any) {
      toast.error('Falha ao assumir: ' + (e?.message || String(e)));
    } finally {
      setIsAssumindo(false);
    }
  }

  useEffect(() => {
    if (!conversation?.id || !podeEscrever || isGrupoInterno || isLoading || targetMessageId
      || (conversation.unread_count || 0) <= 0) return;
    const ultima = messages.filter(m => m.conversation_id === conversation.id && m.direction === 'inbound' && !m.is_private_note)
      .sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id))[0];
    if (!ultima) return;
    let enviada = false;
    const registrar = async () => {
      const el = scrollRef.current;
      if (enviada || document.visibilityState !== 'visible' || !document.hasFocus()
        || !el || el.scrollHeight - el.scrollTop - el.clientHeight > 80) return;
      enviada = true;
      const { data, error } = await callRpc<boolean>('whatsapp_registrar_leitura', {
        p_conversation_id: conversation.id, p_mensagem_id: ultima.id,
        p_unread_count: conversation.unread_count, p_last_inbound_at: conversation.last_inbound_at,
      });
      if (error) { enviada = false; console.error('[Inbox] Falha ao registrar leitura', error); return; }
      if (data === true) {
        for (const key of ['whatsapp-conversations', 'whatsapp-conversations-counts']) {
          void queryClient.invalidateQueries({ queryKey: [key] }, { cancelRefetch: false });
        }
      }
    };
    const el = scrollRef.current;
    const raf = requestAnimationFrame(() => { void registrar(); });
    document.addEventListener('visibilitychange', registrar);
    window.addEventListener('focus', registrar);
    el?.addEventListener('scroll', registrar, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', registrar);
      window.removeEventListener('focus', registrar);
      el?.removeEventListener('scroll', registrar);
    };
  }, [conversation?.id, conversation?.unread_count, conversation?.last_inbound_at,
    podeEscrever, isGrupoInterno, isLoading, targetMessageId, messages, queryClient]);

  // Lazy-fetch foto do cliente (Evolution) quando abrir conversa sem avatar
  const avatarFetchedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const convId = conversation?.id;
    if (!convId) return;
    if (conversation?.contact_avatar_url) return;
    if (!conversation?.connection_id) return;
    if (avatarFetchedRef.current.has(convId)) return;
    avatarFetchedRef.current.add(convId);
    supabase.functions
      .invoke('whatsapp-fetch-avatar', { body: { conversation_id: convId } })
      .catch((e) => console.warn('[ChatWindow] fetch-avatar falhou:', e?.message || e));
  }, [conversation?.id, conversation?.contact_avatar_url, conversation?.connection_id]);

  // Sanitizar mensagens pending antigas via RPC segura (SECURITY DEFINER)
  useEffect(() => {
    if (!conversation?.id || !messages.length) return;
    const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    const hasStale = messages.some(
      m => m.direction === 'outbound' && m.status === 'pending' && !m.provider_message_id && m.created_at < fiveMinAgo
    );
    if (!hasStale) return;
    console.log(`[ChatWindow] Sanitizando msgs pending antigas via RPC`);
    supabase.rpc('whatsapp_mark_stale_pending_as_failed', { p_conversation_id: conversation.id }).then(() => {});
  }, [conversation?.id, messages]);

  // Apagar todas as mensagens falhadas da conversa
  const handleDeleteFailed = async () => {
    if (!conversation?.id) return;
    const { data, error } = await supabase.rpc('whatsapp_delete_failed_messages', { p_conversation_id: conversation.id });
    if (error) {
      toast.error('Não limpou as mensagens que falharam. Tente de novo.');
      return;
    }
    toast.success(`${data || 0} mensagem(ns) removida(s)`);
  };

  // Envia nota interna direto do banner "está atendendo" — sem precisar
  // assumir o atendimento. Insere whatsapp_messages com is_private_note=true,
  // não toca em owner_user_id, mantém quem está atendendo (IA ou outro).
  const handleEnviarNotaDoBanner = async () => {
    if (!conversation || !bannerNoteText.trim() || bannerNoteSending) return;
    const txt = bannerNoteText.trim();
    setBannerNoteSending(true);
    try {
      const resultado = await postConversationAction<{ acionado: boolean; motivo?: string; ia_pausada?: boolean }>(
        `/v1/whatsapp/conversas/${conversation.id}/nota-para-ia`, { nota: txt },
      );
      if (resultado.ia_pausada) toast.success('Nota salva — a IA parou nesta conversa. Assuma para responder.');
      else if (resultado.acionado) toast.success('Nota salva — a IA foi acionada para ler e agir.');
      else toast.warning(resultado.motivo || 'Nota salva, mas a IA não foi acionada.');
      void sincronizarMensagens();
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] }, { cancelRefetch: false });
      setBannerNoteText('');
      setBannerNoteOpen(false);
    } catch (e: any) {
      toast.error('Erro ao salvar nota: ' + (e?.message || e));
    } finally {
      setBannerNoteSending(false);
    }
  };

  // corpoForcado: usado pelo reenvio automatico depois de o backend voltar — o
  // texto ja saiu do campo, entao ele vem por parametro em vez do state.
  const handleSend = async (corpoForcado?: string) => {
    const msgBody = (corpoForcado ?? text).trim();
    if (!msgBody || !conversation || isSending) return;

    const internal = isInternalMode;
    const convId = conversation.id;
    // Nota para a IA nao entra no reenvio automatico: ela aciona a Isabella, e
    // repetir acionaria de novo. Mensagem e nota comum sao gravadas por INSERT,
    // que ou aconteceu ou nao — dai o reenvio ser seguro.
    const ehNotaParaIa = internal && iaAtendendoAgora;
    let mensagemCriada = false;
    // No reenvio automatico o corpo vem por parametro e o campo pode ja ter
    // outra frase digitada durante a espera — limpar ali apagaria o trabalho
    // do consultor. Só o envio manual limpa o campo.
    if (corpoForcado === undefined) setText('');
    setIsSending(true);

    try {
      if (internal && iaAtendendoAgora) {
        const resultado = await postConversationAction<{ acionado: boolean; motivo?: string; ia_pausada?: boolean }>(
          `/v1/whatsapp/conversas/${conversation.id}/nota-para-ia`, { nota: msgBody },
        );
        if ((resultado as any).ia_pausada) toast.success('Nota salva — a IA parou nesta conversa. Assuma para responder.');
        else if (resultado.acionado) toast.success('Nota salva — a IA foi acionada para ler e agir.');
        else toast.warning(resultado.motivo || 'Nota salva, mas a IA não pôde ser acionada agora.');
        void sincronizarMensagens();
        queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] }, { cancelRefetch: false });
        setIsInternalMode(false);
        return;
      }
      // 1. Gravar mensagem outbound.
      // Nota interna e grupo interno entram direto como 'sent' — não há
      // provider externo pra esperar confirmação, então pulam o ciclo
      // pending→sent (que ficava com spinner por 1-2s desnecessariamente).
      const entraComoSent = internal || (isGrupoInterno && !ehGrupoWhats);
      const { data: msgData, error: msgErr } = await supabase
        .from('whatsapp_messages')
        .insert({
          conversation_id: conversation.id,
          direction: 'outbound',
          type: 'text',
          body: msgBody,
          status: entraComoSent ? 'sent' : 'pending',
          message_source: 'humano',
          sent_by_user_id: profile?.id,
          is_private_note: internal,
          ...(replyTo ? { reply_to_message_id: replyTo.id } : {}),
        })
        .select()
        .single();

      if (msgErr) throw msgErr;
      mensagemCriada = true;
      void sincronizarMensagens();

      // Nota interna: NÃO envia para o WhatsApp, apenas atualiza preview interno.
      if (internal) {
        await supabase
          .from('whatsapp_conversations')
          .update({
            last_message_at: new Date().toISOString(),
            last_message_preview: '📝 ' + msgBody.slice(0, 110),
          })
          .eq('id', conversation.id);
        setIsInternalMode(false);
        return;
      }

      // GRUPO INTERNO: mensagem já entrou como 'sent' direto. Só precisa
      // atualizar preview da conversa pra lista refletir. Realtime entrega
      // aos demais membros automaticamente. ZERO chamada externa.
      if (isGrupoInterno) {
        if (ehGrupoWhats) await enviarNoGrupoWhats(msgData?.id);
        await supabase
          .from('whatsapp_conversations')
          .update({
            last_message_at: new Date().toISOString(),
            last_message_preview: msgBody.slice(0, 120),
            last_message_direction: 'outbound',
            last_message_status: 'sent',
            last_outbound_at: new Date().toISOString(),
          })
          .eq('id', conversation.id);
        // Menções: notifica só quem continua citado no texto final
        if (grupoIdAtivo) {
          const citados = mencoesRef.current.filter((t) => msgBody.includes(t.token));
          if (citados.length > 0) {
            notificarMencoes(grupoIdAtivo, citados.map((t) => t.id), msgBody.slice(0, 150)).catch(() => {});
          }
          mencoesRef.current = [];
          marcarLidoMut.mutate(grupoIdAtivo);
        }
        setReplyTo(null);
        // Garante que o grupo SOBE na lista na hora (sem esperar realtime)
        queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
        return;
      }

      // 2. Enviar (provider 360dialog ou Evolution, decidido pelo helper).
      // Usa wa_id se houver — é o JID real que o Evolution já conhece dessa
      // pessoa (capturado da última msg inbound dela). Linha antiga 12 dígitos
      // (sem o 9 móvel) ficaria com telefone canonicalizado errado se
      // usássemos conversation.telefone — Evolution rejeitaria com 400.
      // FASE 3 (26/05): usa helper canônico em vez de wa_id direto.
      // Garante DDI BR + "9" móvel obrigatório. Antes podia mandar pra
      // 12 dígitos sem o "9" e WhatsApp não entregava silenciosamente.
      const toNumber = getDestinationPhone(conversation);
      const quotedProviderId = (replyTo as any)?.provider_message_id || null;
      const { data: sendResult, error: sendErr } = await sendWhatsAppMessage({
        to: toNumber,
        text: msgBody,
        message_id: msgData?.id,
        conversation_id: conversation.id,
        ...(quotedProviderId ? { quoted_provider_id: quotedProviderId } : {}),
      } as any);
      setReplyTo(null);

      if (sendErr) throw sendErr;

      // Checar se a edge function retornou success: false
      if (sendResult && sendResult.success === false) {
        // Linha caida: nada de erro na cara. A msg nao saiu -> some com ela, a saude e' re-lida
        // (o chip + botao aparecem sozinhos) e um aviso curto e discreto. (Douglas 10/07)
        if (sendResult.erro_codigo === 'linha_caida') {
          if (msgData?.id) await supabase.from('whatsapp_messages').delete().eq('id', msgData.id);
          queryClient.invalidateQueries({ queryKey: ['saude-conexoes-global'] });
          toast('Sua linha está fora do ar — siga pela sua outra linha.');
          return;
        }
        const friendly = whatsAppErrorToToast(
          sendResult.erro_codigo,
          sendResult.erro_detalhe || sendResult.error
        );
        throw new Error(friendly);
      }

      // 3. Atualizar status e preview da conversa
      await supabase
        .from('whatsapp_conversations')
        .update({
          last_message_preview: msgBody.slice(0, 120),
          last_message_at: new Date().toISOString(),
          last_outbound_at: new Date().toISOString(),
          status:
            conversation.status === 'nova' || conversation.status === 'aguardando_cliente'
              ? 'em_atendimento'
              : conversation.status,
        })
        .eq('id', conversation.id);

      // 5. (§6) avisa o host: mensagem AO CLIENTE saiu — nota interna não conta.
      if (!internal) onMessageSent?.();
    } catch (err: any) {
      // O backend caiu no meio (deploy/restart) e nada foi gravado? Entao a
      // mensagem nao se perde: fica guardada e sai sozinha quando ele voltar.
      if (!mensagemCriada && !ehNotaParaIa && (await servidorEstaFora())) {
        try { localStorage.setItem(`wa-draft-${convId}`, msgBody); } catch { /* draft best-effort */ }
        setReenvioPendente({ corpo: msgBody, convId });
        return;
      }
      // Erro de verdade: o texto volta pro campo em vez de sumir. Se o consultor
      // ja escreveu outra coisa, o que ele escreveu manda.
      setText((atual) => atual || msgBody);
      toast.error('Erro ao enviar mensagem: ' + err.message);
    } finally {
      setIsSending(false);
    }
  };

  // Espera o backend voltar e manda sozinho o que ficou guardado.
  // Trocar de conversa CANCELA o reenvio: o texto continua salvo como rascunho
  // daquela conversa (o efeito de troca de conversa o devolve ao campo). Sem
  // esse corte, o rascunho voltaria na tela E o reenvio sairia depois — dois
  // envios da mesma frase.
  useEffect(() => {
    if (!reenvioPendente) return;
    if (reenvioPendente.convId !== conversation?.id) { setReenvioPendente(null); return; }
    let cancelado = false;
    (async () => {
      const voltou = await esperarServidorVoltar();
      if (cancelado) return;
      setReenvioPendente(null);
      if (!voltou) {
        setText((atual) => atual || reenvioPendente.corpo);
        toast.error('O servidor não voltou. Sua mensagem está de volta no campo.');
        return;
      }
      try { localStorage.removeItem(`wa-draft-${reenvioPendente.convId}`); } catch { /* draft best-effort */ }
      await handleSend(reenvioPendente.corpo);
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reenvioPendente, conversation?.id]);

  // Função-base que recebe File diretamente, pra reuso entre o input do botão
  // (handleFileUpload) e o paste do clipboard (handlePaste). Antes a lógica
  // vivia só em handleFileUpload e só aceitava ChangeEvent.
  const enviarArquivo = async (file: File) => {
    if (!file || !conversation || isUploading) return;

    setIsUploading(true);
    try {
      // Detect media type
      let mediaType: 'image' | 'audio' | 'video' | 'document' = 'document';
      if (file.type.startsWith('image/')) mediaType = 'image';
      else if (file.type.startsWith('audio/')) mediaType = 'audio';
      else if (file.type.startsWith('video/')) mediaType = 'video';

      // Validar formatos de áudio suportados pelo WhatsApp
      if (mediaType === 'audio') {
        if (!isManualAudioSupported(file.name, file.type)) {
          toast.error('Formato de áudio não suportado pelo WhatsApp. Envie arquivos .ogg, .mp3, .m4a, .aac ou .amr.');
          setIsUploading(false);
          return;
        }
      }

      const ext = file.name.split('.').pop() || 'bin';
      const filePath = `${conversation.id}/${crypto.randomUUID()}.${ext}`;

      // 1. Upload to Supabase Storage
      const { error: upErr } = await supabase.storage
        .from('whatsapp-media')
        .upload(filePath, file, { contentType: file.type, upsert: true });
      if (upErr) throw upErr;

      const { data: urlData } = supabase.storage.from('whatsapp-media').getPublicUrl(filePath);
      const publicUrl = urlData?.publicUrl;
      if (!publicUrl) throw new Error('Falha ao obter URL pública');

      // Preview text
      const previewMap: Record<string, string> = {
        image: '📷 Imagem',
        audio: '🎤 Áudio',
        video: '🎥 Vídeo',
        document: `📄 ${file.name}`,
      };

      // 2. Save outbound message.
      // Grupo interno: insere já como 'sent' (sem ciclo pending→sent).
      const { data: msgData, error: msgErr } = await supabase
        .from('whatsapp_messages')
        .insert({
          conversation_id: conversation.id,
          direction: 'outbound',
          type: mediaType,
          body: mediaType === 'document' ? file.name : null,
          media_url: publicUrl,
          mime_type: file.type,
          status: isGrupoInterno && !ehGrupoWhats ? 'sent' : 'pending',
          message_source: 'humano',
          sent_by_user_id: profile?.id,
        })
        .select()
        .single();
      if (msgErr) throw msgErr;

      // GRUPO INTERNO: mídia fica só no banco (storage + msg).
      if (isGrupoInterno) {
        if (ehGrupoWhats) await enviarNoGrupoWhats(msgData?.id);
        await supabase
          .from('whatsapp_conversations')
          .update({
            last_message_at: new Date().toISOString(),
            last_message_preview: previewMap[mediaType] || 'Mídia',
            last_message_direction: 'outbound',
            last_message_status: 'sent',
            last_outbound_at: new Date().toISOString(),
          })
          .eq('id', conversation.id);
        return;
      }

      // 3. Send (provider 360dialog ou Evolution, decidido pelo helper)
      // Usa wa_id se houver — é o JID real conhecido pelo Evolution.
      const toNumberMedia = getDestinationPhone(conversation);
      const { data: sendResult, error: sendErr } = await sendWhatsAppMessage({
        to: toNumberMedia,
        media_url: publicUrl,
        media_type: mediaType,
        mime_type: file.type,
        filename: file.name,
        conversation_id: conversation.id,
        message_id: msgData?.id,
      });
      if (sendErr) throw sendErr;

      // Checar se a edge function retornou success: false (erro do provedor)
      if (sendResult && sendResult.success === false) {
        const friendly = whatsAppErrorToToast(
          sendResult.erro_codigo,
          sendResult.erro_detalhe || sendResult.error
        );
        throw new Error(friendly);
      }

      // 4. Update conversation preview
      await supabase
        .from('whatsapp_conversations')
        .update({
          last_message_preview: previewMap[mediaType],
          last_message_at: new Date().toISOString(),
          last_outbound_at: new Date().toISOString(),
        })
        .eq('id', conversation.id);


      toast.success('Arquivo enviado!');
    } catch (err: any) {
      toast.error('Erro ao enviar arquivo: ' + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  // Wrapper pro input <file>: extrai o File do event e delega pra enviarArquivo.
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target instanceof HTMLInputElement) e.target.value = '';
    if (file) await enviarArquivo(file);
  };

  // ─── Paste image no composer (Ctrl+V / Cmd+V) ───────────────
  // Douglas 26/05: consultor copia print do celular/desktop e cola direto no
  // chat (igual WhatsApp Web). Mostra preview compacto pra confirmar antes
  // de mandar — evita envio acidental quando alguém só queria colar texto
  // e a clipboard tinha imagem residual.
  const [pastedImage, setPastedImage] = useState<{ file: File; previewUrl: string } | null>(null);

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          // Renomeia pra ficar previsível (timestamp + ext do mime)
          const ext = file.type.split('/')[1]?.split(';')[0] || 'png';
          const renamed = new File([file], `colado-${Date.now()}.${ext}`, { type: file.type });
          const previewUrl = URL.createObjectURL(renamed);
          setPastedImage({ file: renamed, previewUrl });
          return;
        }
      }
    }
  };

  const handleConfirmPasteSend = async () => {
    if (!pastedImage) return;
    const file = pastedImage.file;
    // Limpa preview antes de iniciar upload (UX: usuario ve sumir e barra
    // de "enviando" aparecer)
    URL.revokeObjectURL(pastedImage.previewUrl);
    setPastedImage(null);
    await enviarArquivo(file);
  };

  const handleCancelPaste = () => {
    if (pastedImage) URL.revokeObjectURL(pastedImage.previewUrl);
    setPastedImage(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Se há imagem colada esperando confirmação, Enter envia ela em vez do texto
    if (e.key === 'Enter' && !e.shiftKey && pastedImage) {
      e.preventDefault();
      handleConfirmPasteSend();
      return;
    }
    if (e.key === 'Escape' && pastedImage) {
      e.preventDefault();
      handleCancelPaste();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const cleanupRecording = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (oggRecorderRef.current) {
      try { oggRecorderRef.current.close(); } catch {}
      oggRecorderRef.current = null;
    }
    oggFilePromiseRef.current = null;
    setRecordingDuration(0);
    setIsRecording(false);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const recorder = createOggRecorder();
      oggRecorderRef.current = recorder;

      // startOggRecording configura os handlers e retorna promise que resolve ao parar
      oggFilePromiseRef.current = startOggRecording(recorder);

      // start() faz getUserMedia internamente e inicia encoding WASM
      await recorder.start();
      console.log('[ChatWindow] Gravação OGG/Opus via WASM iniciada');

      setIsRecording(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('[ChatWindow] Falha ao iniciar gravador OGG/Opus:', err);
      cleanupRecording();
      toast.error('Não foi possível iniciar o gravador de áudio. Envie um arquivo .ogg/.mp3/.m4a.');
    }
  }, [cleanupRecording]);

  const cancelRecording = useCallback(() => {
    cleanupRecording();
  }, [cleanupRecording]);

  const sendRecording = useCallback(async () => {
    const recorder = oggRecorderRef.current;
    const filePromise = oggFilePromiseRef.current;
    if (!recorder || !filePromise) return;

    try {
      // stop() finaliza encoding e dispara onstop → resolve filePromise
      await recorder.stop();
      const file = await filePromise;

      cleanupRecording();

      if (!file) {
        toast.error('Não foi possível gerar áudio compatível com WhatsApp. Envie um arquivo .ogg/.mp3/.m4a.');
        return;
      }

      const mime = 'audio/ogg';
      console.log(`[ChatWindow] Áudio final: OGG/Opus via WASM (${file.size} bytes)`);

      if (!conversation) return;

      setIsUploading(true);

      // Upload para Supabase Storage
      const filePath = `${conversation.id}/${crypto.randomUUID()}.ogg`;
      const { error: upErr } = await supabase.storage
        .from('whatsapp-media')
        .upload(filePath, file, { contentType: mime, upsert: true });
      if (upErr) throw upErr;

      const { data: urlData } = supabase.storage.from('whatsapp-media').getPublicUrl(filePath);
      const publicUrl = urlData?.publicUrl;
      if (!publicUrl) throw new Error('Falha ao obter URL pública');

      // Gravar mensagem outbound.
      // Grupo interno: insere já como 'sent' (sem ciclo pending→sent).
      const { data: msgData, error: msgErr } = await supabase
        .from('whatsapp_messages')
        .insert({
          conversation_id: conversation.id,
          direction: 'outbound',
          type: 'audio',
          body: null,
          media_url: publicUrl,
          mime_type: mime,
          status: isGrupoInterno && !ehGrupoWhats ? 'sent' : 'pending',
          message_source: 'humano',
          sent_by_user_id: profile?.id,
        })
        .select()
        .single();
      if (msgErr) throw msgErr;

      // GRUPO INTERNO: áudio fica só no banco/storage.
      if (isGrupoInterno) {
        if (ehGrupoWhats) await enviarNoGrupoWhats(msgData?.id);
        await supabase
          .from('whatsapp_conversations')
          .update({
            last_message_at: new Date().toISOString(),
            last_message_preview: '🎤 Áudio',
            last_message_direction: 'outbound',
            last_message_status: 'sent',
            last_outbound_at: new Date().toISOString(),
          })
          .eq('id', conversation.id);
        setIsUploading(false);
        return;
      }

      // Enviar (provider 360dialog ou Evolution, decidido pelo helper) — voice=true
      // FASE 3 (26/05): usa helper canônico em vez de wa_id direto.
      const toNumberAudio = getDestinationPhone(conversation);
      const { data: sendResult, error: sendErr } = await sendWhatsAppMessage({
        to: toNumberAudio,
        media_url: publicUrl,
        media_type: 'audio' as const,
        mime_type: mime,
        filename: file.name,
        conversation_id: conversation.id,
        message_id: msgData?.id,
        voice: true,
      });
      if (sendErr) throw sendErr;
      if (sendResult && sendResult.success === false) {
        if (msgData?.id) {
          await supabase
            .from('whatsapp_messages')
            .update({
              status: 'failed',
              erro_codigo: sendResult.erro_codigo || null,
              erro_detalhe: sendResult.erro_detalhe || sendResult.error || null,
            })
            .eq('id', msgData.id);
        }
        const friendly = whatsAppErrorToToast(
          sendResult.erro_codigo,
          sendResult.erro_detalhe || sendResult.error
        );
        throw new Error(friendly);
      }

      // Atualizar preview da conversa
      await supabase
        .from('whatsapp_conversations')
        .update({
          last_message_preview: '🎤 Áudio',
          last_message_at: new Date().toISOString(),
          last_outbound_at: new Date().toISOString(),
        })
        .eq('id', conversation.id);


      toast.success('Áudio enviado!');
    } catch (err: any) {
      toast.error('Erro ao enviar áudio: ' + err.message);
    } finally {
      setIsUploading(false);
    }
  }, [cleanupRecording, conversation, profile?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (oggRecorderRef.current) {
        try { oggRecorderRef.current.close(); } catch {}
      }
    };
  }, []);

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // INBOX EVOLUTION-ONLY: não existe janela 24h / template (360dialog/Oficial abolido).

  const HEADER_STATUS_DOT: Record<string, string> = {
    nova: 'bg-success',
    em_atendimento: 'bg-primary',
    aguardando_cliente: 'bg-primary',
    aguardando_interno: 'bg-primary',
    convertida: 'bg-success',
    finalizada: 'bg-muted-foreground',
    arquivada: 'bg-muted-foreground/50',
  };
  const headerStatusDot = conversation
    ? (HEADER_STATUS_DOT[conversation.status] || 'bg-muted-foreground')
    : 'bg-muted-foreground';
  const headerStatusLabel = conversation
    ? (STATUS_LABELS[conversation.status] || conversation.status)
    : '';

  // Grupo interno nunca "finaliza" — não tem ciclo de atendimento.
  const isFinalizada = !isGrupoInterno &&
    (conversation?.status === 'finalizada' || conversation?.status === 'arquivada');

  /* ⛔ UM BOTÃO SÓ, E ELE FINALIZA (Douglas 17/09/2026): "assim ninguém
     consegue fechar a conversa, mas sim finalizar".
     Antes havia "Fechar" (conversa vazia) aqui no topo e "Encerrar conversa"
     escondido no painel, em Encaminhar — dois nomes para a MESMA rota. Só esta
     rota faz as três coisas: marca a conversa, desliga a IA e fecha o ciclo de
     `ia_agente_conversas`. Um UPDATE de status direto faz só a primeira e deixa
     a IsabellA muda quando o cliente volta com assunto novo. */
  const handleFinalizarConversa = async () => {
    if (!conversation || isClosingEmpty) return;
    setIsClosingEmpty(true);
    try {
      await postConversationAction(`/v1/whatsapp/conversations/${conversation.id}/finalizar`);
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations-counts'] });
      toast.success('Atendimento finalizado');
      onConversationCleared?.();
    } catch (error) {
      toast.error(`Não finalizou: ${error instanceof Error ? error.message : 'atualize a tela e tente novamente'}`);
    } finally {
      setIsClosingEmpty(false);
    }
  };

  const handleReabrirConversa = async () => {
    if (!conversation || isClosingEmpty) return;
    setIsClosingEmpty(true);
    try {
      await postConversationAction(`/v1/whatsapp/conversations/${conversation.id}/reabrir`);
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations-counts'] });
      onConversationReopened?.();
      toast.success('Conversa reaberta');
    } catch (error) {
      toast.error(`Não reabriu: ${error instanceof Error ? error.message : 'atualize a tela e tente novamente'}`);
    } finally {
      setIsClosingEmpty(false);
    }
  };

  if (!conversation) {
    return (
      <div className="flex flex-1 items-center justify-center bg-surface pb-24">
        <div className="text-center space-y-3 p-8">
          <div className="w-[58px] h-[58px] mx-auto rounded-full bg-foreground/[0.06] flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-primary" strokeWidth={1.5} />
          </div>
          <div>
            <p className="font-display text-[17px] font-bold text-foreground">WhatsApp CRM</p>
            <p className="text-[12.5px] text-muted-foreground mt-1">
              Selecione uma conversa para começar o atendimento
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground justify-center">
            <span className="w-1.5 h-1.5 rounded-full bg-success inline-block animate-pulse" />
            API Oficial conectada
          </div>
        </div>
      </div>
    );
  }

  // Agrupar por data para separadores
  const msgGroups: { date: string; msgs: typeof messages }[] = [];
  let lastDate = '';
  mensagensTela.forEach((m) => {
    const d = new Date(m.created_at).toDateString();
    if (d !== lastDate) {
      lastDate = d;
      msgGroups.push({ date: m.created_at, msgs: [] });
    }
    msgGroups[msgGroups.length - 1].msgs.push(m);
  });

  const displayName =
    conversation.contact_name ||
    conversation.destinatario_razao_social ||
    conversation.destinatario_nome ||
    conversation.telefone;

  // Linha OFICIAL (Bee/Cloud API) tem tom próprio no cabeçalho — faixa bordô e
  // o contador da janela de 24h ao lado do nome da linha (Douglas 25/09/2026,
  // opção B). A janela vem da régua única: can_reply do Bee + mensagem do cliente.
  const ehLinhaOficial = conversation?.connection_provider === 'bee360' && !conversation?.chat_grupo_id;
  const janelaHeader = ehLinhaOficial ? getWindowGhost(conversation, nowTick) : null;

  return (
    <div className="flex-1 flex flex-col h-full min-w-0">
      {/* Header */}
      <div className="relative flex flex-shrink-0 items-center gap-3 border-b border-border bg-card px-4 py-1.5">
        {ehLinhaOficial && (
          <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-[#5B0A0D] dark:bg-[#9D2530]" />
        )}
        {onBack && (
          <Button variant="ghost" size="icon" className="h-7 w-7 lg:hidden" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        {/* FOTO DO CONTATO AMPLIA, igual ao WhatsApp (Douglas 21/08/2026: "eu clico
            na imagem e ela nao abre maior como faz no whats"). Usa o MESMO
            lightbox das imagens da conversa — zoom, arrastar, ESC. Sem foto, o
            avatar continua sendo so a inicial e nao vira botao a toa. */}
        {conversation?.contact_avatar_url ? (
          <button
            type="button"
            onClick={() => setPreviewImage({
              url: conversation.contact_avatar_url!,
              name: displayName || 'Foto do contato',
            })}
            title="Ver a foto"
            className="rounded-full transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <WhatsAppAvatar nome={displayName} fotoUrl={conversation.contact_avatar_url} size="md" />
          </button>
        ) : (
          <WhatsAppAvatar nome={displayName} fotoUrl={conversation?.contact_avatar_url} size="md" />
        )}
        <div className="flex-1 min-w-0">
          {/* Linha 1 — status dot + nome + janela 24h ghost */}
          <div className="flex items-center gap-2">
            <span
              className={cn('h-1.5 w-1.5 rounded-full flex-shrink-0', headerStatusDot)}
              title={headerStatusLabel}
              aria-label={headerStatusLabel}
            />
            {/* No celular, tocar no nome abre o Painel CRM, como os dados do
                contato no WhatsApp. No computador o painel já está ao lado. */}
            {onAbrirPainel && (
              <button
                type="button"
                onClick={onAbrirPainel}
                title="Abrir o Painel CRM"
                className="min-w-0 truncate text-left text-sm font-semibold lg:hidden"
              >
                {displayName}
              </button>
            )}
            <span className={cn('text-sm font-semibold truncate', onAbrirPainel && 'hidden lg:inline')}>{displayName}</span>
            {/* Era âmbar COM emoji — as duas coisas que a lei visual da casa não
                permite. A lista já tinha sido corrigida na main; aqui tinha ficado. */}
            {conversation?.campanha_id && conversation.liberada_consultor !== true && campanhasIaOnly?.has(conversation.campanha_id) && (
              <span
                className="inline-flex flex-shrink-0 items-center gap-1 rounded border border-primary/40 bg-primary/10 px-1.5 py-0 text-[10px] font-semibold text-primary"
                title="Campanha Apenas IA — oculta dos consultores, só o master vê"
              >
                <Lock className="h-2.5 w-2.5" /> Só Master
              </span>
            )}
          </div>
          {/* Linha 2 — metadados sutis em texto, sem badges nem caixinhas */}
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground/80 truncate">
            {conversation.connection_nome && (
              <>
                <span
                  className={cn('inline-flex max-w-[200px] flex-shrink-0 items-center gap-1 text-[10px] font-semibold leading-4',
                    ehLinhaOficial ? 'text-[#5B0A0D] dark:text-[#C87484]' : 'text-primary/85 dark:text-brand-header')}
                  title={ehLinhaOficial
                    ? `Linha oficial (API da Meta): ${formatConnectionName(conversation.connection_nome)}. Fora da janela de 24h só sai mensagem aprovada.`
                    : `Conexão real: ${formatConnectionName(conversation.connection_nome)}. Assumir mantém esta conexão.`}
                >
                  {ehLinhaOficial
                    ? <ShieldCheck className="h-2.5 w-2.5 flex-shrink-0" />
                    : <Link2 className="h-2.5 w-2.5 flex-shrink-0" />}
                  <span className="truncate">{formatConnectionName(conversation.connection_nome)}</span>
                </span>
                {janelaHeader && (
                  <>
                    <span className="text-muted-foreground/40">·</span>
                    <span
                      className={cn('inline-flex flex-shrink-0 items-center gap-1 text-[10.5px] tabular-nums', janelaHeader.textCls)}
                      title={janelaHeader.expired
                        ? 'Janela de 24h fechada: até o cliente responder, só sai mensagem aprovada.'
                        : `Janela de 24h aberta: ${janelaHeader.label} para o cliente responder antes de fechar. Mensagem normal sai sem custo.`}
                    >
                      {janelaHeader.expired ? <Lock className="h-2.5 w-2.5" /> : <Clock className="h-2.5 w-2.5" />}
                      {janelaHeader.expired ? 'janela fechada' : `janela ${janelaHeader.label}`}
                    </span>
                  </>
                )}
                {!saudeForaReal && connHealth && connHealth.severity !== 'ok' && (
                  <ConnectionHealthChip
                    nome={conversation.connection_nome}
                    telefone={conversation.connection_telefone}
                    conversationId={conversation.id}
                    health={connHealth}
                  />
                )}
                {saudeForaReal && (
                  <span
                    className="ml-1 inline-flex items-center gap-1 rounded px-1.5 py-0 text-[10px] leading-4 border text-danger bg-danger/10 border-danger/40"
                    title={saudeDe(conversation.connection_id)?.reason === 'envio_mudo'
                      ? 'Conectada no celular, mas as mensagens não estão chegando — fale com o cliente pela sua outra linha'
                      : 'Esta linha está fora do ar — abra a conversa pela sua outra linha'}
                  >
                    <CircleDot className="h-2.5 w-2.5" /> {saudeDe(conversation.connection_id)?.reason === 'envio_mudo' ? 'não entrega' : 'fora do ar'}
                  </span>
                )}
                <span className="text-muted-foreground/40">·</span>
              </>
            )}
            {/* Em grupo interno, telefone é sintético (__GRUPO__xxx) — não mostra. */}
            {!isGrupoInterno && (
              <span className="tabular-nums" title="Telefone do cliente">
                <span className="text-muted-foreground/60">cliente</span> {conversation.telefone}
              </span>
            )}
            {conversation.destinatario_cnpj && (
              <>
                <span className="text-muted-foreground/40">·</span>
                <span
                  className="truncate"
                  title={`CNPJ ${conversation.destinatario_cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')}`}
                >
                  CNPJ {conversation.destinatario_cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')}
                </span>
              </>
            )}
            {/* 🆕 Regra Douglas 25/05/2026: se a conversa TEM dono humano,
                 SEMPRE mostra o nome + equipe do humano (modelo antigo).
                 So mostra a IA quando a conversa esta sem dono.
                 🆕 Pós-expediente: quando a IsabellA está cobrindo a linha agora
                 (iaAtendendoAgora), ela vem na frente mesmo com dono estrutural. */}
            {(!iaAtendendoAgora && conversation.owner_nome) ? (
              <>
                <span className="text-muted-foreground/40">·</span>
                <span
                  className="truncate"
                  title={conversation.owner_equipe ? `${conversation.owner_nome} · ${conversation.owner_equipe.toUpperCase()}` : conversation.owner_nome}
                >
                  <span className="text-muted-foreground/60">atende</span> {conversation.owner_nome}
                  {conversation.owner_equipe && (
                    <span className="text-muted-foreground/80"> · {conversation.owner_equipe.toUpperCase()}</span>
                  )}
                </span>
              </>
            ) : (iaAtendendoAgora || conversation.agente_ia_ativo) && conversation.agente_ia_nome && (
              <>
                <span className="text-muted-foreground/40">·</span>
                <span
                  className="inline-flex items-center gap-1 truncate"
                  title={`Atendido por Agente IA: ${conversation.agente_ia_nome}`}
                >
                  <span className={`inline-flex h-[18px] w-[18px] items-center justify-center rounded-md ${getAgenteCores(conversation.agente_ia_nome).iconBg} text-white shadow-sm`}>
                    <BotMessageSquare className="h-3 w-3" strokeWidth={2.5} />
                  </span>
                  <span className={`font-mono text-xs font-bold tracking-tight ${getAgenteCores(conversation.agente_ia_nome).nameGrad} bg-clip-text text-transparent`}>
                    {getAgenteNomeCurto(conversation.agente_ia_nome)}
                  </span>
                  <span className={`text-xs font-semibold ${getAgenteCores(conversation.agente_ia_nome).labelText}`}>
                    · Agente IA
                  </span>
                  <span className="text-xs text-muted-foreground/80">
                    ({getAgenteEspecialidade(conversation.agente_ia_nome)})
                  </span>
                </span>
              </>
            )}
            {/* DEPRECATED: bloco "campanha_nome" removido em 24/05/2026.
                Conversa nao distingue mais origem (campanha vs direta). */}
          </div>
        </div>
        {/* ⛔ O FINALIZAR MORA AQUI (Douglas 17/09/2026). Saiu daqui o ícone
            de fatura, que abria o MESMO modal já disponível no painel em
            ATENDER → "Solicitar fatura do CNPJ" — ação repetida atrás de um
            ícone mudo. E saiu o "Fechar" da conversa vazia: mesma rota, outro
            nome. Destaque sutil pela BORDA bordô, nunca por fundo — um acento
            por vista, como o resto do Inbox.
            Grupo interno não tem ciclo de atendimento: não finaliza. */}
        {!isGrupoInterno && conversation && <CicloQualidadeFaixa conversaId={conversation.id} />}
        {onAbrirPainel && (
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 flex-shrink-0 border-border-warm bg-card text-muted-foreground hover:border-[#5B0A0D]/40 hover:bg-card hover:text-[#5B0A0D] dark:hover:text-[#C87484] lg:hidden"
            onClick={onAbrirPainel}
            title="Abrir o Painel CRM"
            aria-label="Abrir o Painel CRM"
          >
            <PanelRightOpen className="h-3.5 w-3.5" />
          </Button>
        )}
        {!isGrupoInterno && conversation && (
          isFinalizada ? (
            <Button
              variant="outline"
              size="sm"
              className="h-7 flex-shrink-0 gap-1.5 px-2.5 text-xs"
              onClick={handleReabrirConversa}
              disabled={isClosingEmpty}
              title="Reabre a conversa e devolve ela para as filas do Inbox."
            >
              {isClosingEmpty ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRightLeft className="h-3.5 w-3.5" />}
              Reabrir
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="h-7 flex-shrink-0 gap-1.5 border-border-warm bg-card px-2.5 text-[11.5px] font-semibold text-muted-foreground hover:border-[#5B0A0D]/40 hover:bg-card hover:text-[#5B0A0D] dark:hover:text-[#C87484]"
              onClick={handleFinalizarConversa}
              disabled={isClosingEmpty}
              title="Finaliza o atendimento: a conversa vai para Finalizado e o ciclo da IsabellA é encerrado. Reabre se o cliente voltar."
            >
              {isClosingEmpty ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Finalizar
            </Button>
          )
        )}
        {/* Botão limpar falhadas */}
        {messages.some(m => m.status === 'failed' && m.direction === 'outbound') && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-destructive hover:bg-destructive/10 gap-1 flex-shrink-0"
            onClick={handleDeleteFailed}
            title="Limpar mensagens falhadas"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Limpar falhadas
          </Button>
        )}
      </div>

      {/* O modal de lead manual JÁ resolve os dois passos: acha ou cria o
          cliente pelo documento (fn_get_or_create_cliente por dentro) e cria o
          lead. Não escrevi tela nova — a que existe é a mesma que a Gestão de
          Leads usa, e duas telas de cadastrar cliente divergiriam na primeira
          regra nova.
          ⚠️ E ela JÁ respeita a Lei dos 3 Donos: cliente que é de outro
          consultor não vira seu por ter sido identificado aqui — o lead vai
          para o dono, e a trava `trg_aa_lead_manual_respeita_dono` garante
          isso no banco, não só na tela. */}
      {cadastroAberto && conversation && (
        <ModalCriarLeadManual
          open={cadastroAberto}
          onOpenChange={setCadastroAberto}
          onSuccess={async (leadId: string) => {
            setCadastroAberto(false);
            try {
              await supabase.from('whatsapp_conversations')
                .update({ lead_id: leadId })
                .eq('id', conversation.id);
              queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
              queryClient.invalidateQueries({ queryKey: ['conversa-cartao'] });
              toast.success('Cliente identificado', {
                description: 'A conversa saiu da fila de cadastro e agora segue o dono.',
              });
            } catch (e) {
              toast.error(`Não deu para vincular: ${e instanceof Error ? e.message : String(e)}`);
            }
          }}
          telefoneInicial={conversation.telefone || undefined}
          razaoSocialInicial={conversation.contact_name || undefined}
          origemContexto="whatsapp_inbox"
        />
      )}

      {/* ⛔ A FAIXA DE CADASTRO — o número que ninguém identificou (Douglas
          17/09/2026, aprovado): "vamos criar elas em uma fila com todas
          separadas, e quem pegar, cadastrar e falar vira dono lead."

          São 706 conversas ativas sem documento E sem lead, ~62 novas por dia.
          Elas não existem para o CRM: sem documento não há cliente, sem cliente
          não há lead, e sem lead não há proposta, pedido, carteira nem comissão.

          ⛔ FAIXA, NÃO MODAL. Modal interrompe quem está lendo o que o cliente
          acabou de escrever — e a identificação quase sempre vem do que ELE
          escreveu ("aqui é da empresa tal"). A faixa espera.

          ⛔ E É LINHA, NÃO BOTÃO GRANDE. Douglas: "cuidado com os botões
          grandes, já falei; vocês estão usando eles novamente tanto no enviar
          quanto no identificar." O destaque é a BORDA bordô, como no resto do
          painel.

          Responder continua liberado, sempre: a trava é só no que depende de
          cliente (proposta, pedido, fatura). Quem só está perguntando preço não
          pode ficar sem resposta — são 62 por dia. */}
      {precisaCadastro && (
        <button
          type="button"
          onClick={() => setCadastroAberto(true)}
          className="flex h-[29px] w-full flex-shrink-0 items-center gap-2 border-b border-border bg-card px-3 text-left transition-colors hover:bg-surface"
          title="Este número não está ligado a nenhum cliente. Sem isso não dá para fazer proposta, pedido nem fatura."
        >
          <UserPlus className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
          <span className="text-[11px] text-foreground">Quem é este número?</span>
          <span className="min-w-0 flex-1 truncate text-[10.5px] text-muted-foreground">
            sem CPF ou CNPJ não dá para fazer proposta, pedido nem fatura
          </span>
          <span className="flex-shrink-0 rounded-md border border-primary px-2 py-[2px] text-[10.5px] font-semibold text-primary">
            Identificar
          </span>
        </button>
      )}

      {/* ⛔ A FAIXA DA ETAPA — a correção do caso Raphael aplicada a GENTE.
          (Douglas 16/09/2026.) Um PN com PORTABILIDADE AGENDADA para o dia
          seguinte às 12:00, SMS já respondido, e a IsabellA mandou o cliente
          buscar 2ª via de chip. O dado estava certo no banco; faltava a
          tradução da etapa em ação — e o risco é o mesmo para quem digita:
          ninguém decora 36 status.
          Lê o MESMO dicionário que a IsabellA (`status_catalogo`): tela e IA
          nunca podem dizer coisas diferentes sobre o mesmo pedido.
          Só aparece quando há pedido — em 25% das conversas; nas outras, a
          faixa simplesmente não existe, em vez de ficar vazia. */}
      {etapaDaConversa && (
        <div className="flex flex-shrink-0 items-center gap-2 border-b border-border bg-card px-3 py-1.5">
          <span className="flex-shrink-0 text-[9.5px] font-bold uppercase tracking-wider text-primary">
            {etapaDaConversa.fila}
          </span>
          {etapaDaConversa.prova && (
            <span className="min-w-0 flex-1 truncate text-[11px] text-foreground" title={etapaDaConversa.prova}>
              {etapaDaConversa.prova}
            </span>
          )}
          {/* O que NÃO se diz fica à vista, não escondido num tooltip: é o que
              evita o erro, e erro evitado é o motivo de a faixa existir. */}
          {etapaDaConversa.nao_diga && (
            <span
              className="flex-shrink-0 truncate text-[10.5px] text-muted-foreground"
              title={etapaDaConversa.nao_diga}
            >
              {etapaDaConversa.nao_diga}
            </span>
          )}
        </div>
      )}
      {headerBanner}

      {/* Catalogo de midia — botao + no header abre */}
      {conversation?.id && conversation?.telefone && conversation?.connection_id && (
        <CatalogoPicker
          open={catalogoOpen}
          onClose={() => setCatalogoOpen(false)}
          conversationId={conversation.id}
          telefoneCliente={conversation.telefone}
          connectionId={conversation.connection_id}
        />
      )}
      {/* Aviso: número usado FORA do inbox (aparelho/WhatsApp Web) — risco de queda/ban */}
      {aparelhoRecente > 0 && (
        <div className="flex items-center gap-1.5 px-4 py-1 border-b border-border/60 bg-muted/30 text-muted-foreground text-[11px] flex-shrink-0">
          <span className="text-[10px] leading-none opacity-50">📱</span>
          <div>
            <strong>Este número está sendo usado fora do inbox</strong> ({aparelhoRecente}{' '}
            {aparelhoRecente === 1 ? 'mensagem' : 'mensagens'} pelo aparelho nas últimas 24h).
            Responder pelo WhatsApp Web/celular pode <strong>desconectar ou banir</strong> a conexão — use só o inbox.
          </div>
        </div>
      )}
      {/* Área de mensagens */}
      {targetMessageId && <div className="flex items-center justify-between gap-2 border-b border-border bg-card px-4 py-2 text-xs" role="status">
        <span className="text-muted-foreground">{isLoading ? 'Localizando mensagem original…' : targetError || 'Mensagem original destacada no histórico.'}</span>
        <button type="button" className="shrink-0 text-primary underline" onClick={() => {
          const next = new URLSearchParams(messageParams);
          next.delete('message');
          next.delete('conversation');
          setMessageParams(next, { replace: true });
        }}>Voltar às recentes</button>
      </div>}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto bg-surface px-4 py-3 pb-24"
      >
        {/* Load more */}
        {hasMore && (
          <div className="flex justify-center pb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={carregarAnteriores}
              className="text-xs text-muted-foreground gap-1 bg-card/70 dark:bg-muted/70 backdrop-blur-sm rounded-full"
            >
              <ChevronUp className="h-3.5 w-3.5" />
              Carregar mensagens anteriores
            </Button>
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && messages.length === 0 && (
          <div className="flex items-center justify-center h-32">
            <p className="text-xs text-muted-foreground bg-card/70 dark:bg-muted/70 backdrop-blur-sm px-3 py-1.5 rounded-full">
              Nenhuma mensagem ainda
            </p>
          </div>
        )}

        {msgGroups.map((group) => (
          <React.Fragment key={group.date}>
            {/* Separador de data — estilo WhatsApp */}
            <div className="flex justify-center py-2">
              <span className="text-[11px] text-muted-foreground bg-card/75 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm">
                {formatDateSeparator(group.date)}
              </span>
            </div>
            {/* Mensagens do grupo */}
            <div>
              {group.msgs.map((msg, msgIdx) => (
                <div key={msg.id} ref={msg.id === targetMessageId ? targetElementRef : undefined}
                  data-message-id={msg.id}
                  className={msg.id === targetMessageId ? 'rounded-lg ring-2 ring-primary ring-offset-2 ring-offset-background' : undefined}>
                <MessageBubble
                  msg={msg}
                  prevMsg={msgIdx > 0 ? group.msgs[msgIdx - 1] : null}
                  senders={sendersTela}
                  autoresGrupoWhats={ehGrupoWhats ? autoresGrupo?.porId : undefined}
                  agenteIaNome={conversation?.agente_ia_nome}
                  onPreview={(url) => setPreviewImage({ url, name: 'Imagem' })}
                  isGrupo={isGrupoInterno}
                  meuUserId={profile?.id}
                  grupo={isGrupoInterno ? {
                    reacoes: reacoesMap?.get(msg.id) || [],
                    onReply: () => setReplyTo(msg),
                    onReact: (emoji) => reagirMut.mutate({ message_id: msg.id, emoji, conversation_id: conversation!.id }),
                    onDelete: () => apagarMut.mutate({ message_id: msg.id, conversation_id: conversation!.id }),
                    podeApagar: msg.sent_by_user_id === profile?.id || souAdminDoGrupo,
                    lidoPorTodos: !!lidoPorTodosAte && msg.created_at <= lidoPorTodosAte,
                    quoted: resolverQuoted(msg),
                  } : (podeEscrever && !msg.is_private_note ? {
                    // Conversa REAL: reagir (propaga pro WhatsApp) + citar.
                    // Sem apagar; recibo continua o nativo (StatusIcon).
                    reacoes: reacoesMap?.get(msg.id) || [],
                    onReply: () => setReplyTo(msg),
                    onReact: (emoji) => reagirRealMut.mutate({ message_id: msg.id, emoji, conversation_id: conversation!.id }),
                    podeApagar: false,
                    quoted: resolverQuoted(msg),
                  } : undefined)}
                />
                </div>
              ))}
            </div>
          </React.Fragment>
        ))}

        <div ref={bottomRef} />
      </div>

      {/* Banner 24h expirada — só mostra se NÃO for exibir o composer de template */}

      {/* (Indicador de bloqueio movido para um chip discreto no header — ver ConnectionHealthChip) */}

      {composerBanner}

      {/* Composer */}
      <div className={`flex-shrink-0 border-t border-border bg-card ${bannerBloqueia ? '' : 'px-4 py-2'}`}>
        {isFinalizada ? (
          <p className="text-center text-xs text-muted-foreground py-2">
            Conversa finalizada — reabra para responder
          </p>
        ) : !podeEscrever && !!conversation && conversation.status !== 'finalizada' && conversation.status !== 'arquivada' ? (
          // BLOQUEIO DE ESCRITA — voce nao e o dono da conversa.
          // Mostra quem esta atendendo (IA ou consultor X) + botao Assumir.
          // Se IA tem visao com agente_ativo na conexao -> "IA atendendo".
          // Senao -> "Sem dono" ou "[Nome do colega] atendendo".
          (() => {
            const temIA = iaAtendendoAgora;
            const outroConsultor = !!conversation.owner_user_id && conversation.owner_user_id !== profile?.id;
            const nomeOutro = (conversation as any).owner_nome || 'Outro consultor';
            const iaNome = conversation.agente_ia_nome || 'IA';
            const conexao = conversation.connection_nome ? formatConnectionName(conversation.connection_nome) : null;

            if (permissaoEscrita.data && !permissaoEscrita.data.assumir.permitido) return null;
            let titulo: string;
            let sub: string;
            let bgClass: string;
            let icone: React.ReactNode;
            if (permissaoEscrita.isError || !permissaoEscrita.data) {
              titulo = permissaoEscrita.isError ? 'Não foi possível validar a permissão' : 'Verificando permissão de atendimento';
              sub = 'A escrita será liberada após a confirmação do servidor.';
              bgClass = 'text-foreground';
              icone = <Lock className="h-4 w-4 text-primary" />;
            } else if (temIA) {
              titulo = `${iaNome} está atendendo esta conversa`;
              sub = 'Você está só visualizando. Clique pra responder no lugar da IA.';
              bgClass = 'text-foreground';
              icone = <BotMessageSquare className="h-4 w-4 text-primary" />;
            } else if (outroConsultor) {
              titulo = `${nomeOutro} está atendendo`;
              sub = donoDoCliente && donoDoCliente.id !== conversation.owner_user_id
                ? `Cliente de ${donoDoCliente.nome}. Você pode acompanhar, anotar ou assumir; o dono não muda.`
                : conexao
                ? `Você pode acompanhar e anotar. Assumir mantém a conexão ${conexao}.`
                : 'Você pode acompanhar, anotar ou assumir o atendimento.';
              bgClass = 'text-foreground';
              icone = <CircleDot className="h-4 w-4 text-primary" />;
            } else if (iaPausadaPorNota) {
              titulo = 'IA parada nesta conversa';
              sub = 'Uma nota interna mandou a IA parar. Assuma para responder.';
              bgClass = 'text-foreground';
              icone = <BotMessageSquare className="h-4 w-4 text-primary" />;
            } else if (donoDoCliente) {
              titulo = `Cliente de ${donoDoCliente.nome}`;
              sub = 'Ninguém está atendendo agora. Assumir não muda o dono do cliente.';
              bgClass = 'text-foreground';
              icone = <CircleDot className="h-4 w-4 text-primary" />;
            } else {
              titulo = 'Conversa Sem Dono';
              sub = 'Ninguém está atendendo. Clique pra assumir.';
              bgClass = 'text-foreground';
              icone = <AlertTriangle className="h-4 w-4 text-primary" />;
            }

            return (
              <div className={cn('space-y-2', bgClass)}>
                <div className="flex items-center gap-2.5">
                  <div className="flex-shrink-0">{icone}</div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-[12.5px] font-semibold leading-tight">{titulo}</p>
                    <p className="mt-0.5 text-[10.5px] leading-tight text-muted-foreground">{sub}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {/* Botão "Nota" — permite escrever nota interna sem assumir.
                        Toggle do mini-composer abaixo. */}
                    <Button
                      onClick={() => setBannerNoteOpen(v => !v)}
                      size="sm"
                      variant="outline"
                      className="h-8 border-border px-2.5 text-foreground/70 hover:border-primary hover:text-primary"
                      title="Escrever nota interna sem assumir o atendimento"
                    >
                      <Lock className="h-3.5 w-3.5 mr-1" />
                      Nota
                    </Button>
                    <Button
                      onClick={handleAssumirAtendimento}
                      disabled={isAssumindo || permissaoEscrita.isError || permissaoEscrita.data?.assumir.permitido !== true}
                      size="sm"
                      className="h-8 bg-primary px-3 font-semibold text-primary-foreground hover:bg-primary/90"
                    >
                      {isAssumindo ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Assumir'}
                    </Button>
                  </div>
                </div>
                {bannerNoteOpen && (
                  <div className="mt-1 space-y-2 border-t border-border/70 pt-2">
                    <div className="flex items-center gap-1.5 text-[11px] font-medium text-primary dark:text-brand-header">
                      <Lock className="h-3 w-3" />
                      Nota interna — visível só pra equipe Prime, não vai pro cliente
                    </div>
                    <textarea
                      spellCheck
                      lang="pt-BR"
                      value={bannerNoteText}
                      onChange={(e) => setBannerNoteText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                          e.preventDefault();
                          handleEnviarNotaDoBanner();
                        }
                      }}
                      rows={2}
                      placeholder="Escreva sua nota… (Ctrl+Enter pra salvar)"
                      autoFocus
                      className="w-full resize-none rounded-md border bg-surface px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setBannerNoteOpen(false); setBannerNoteText(''); }}
                        disabled={bannerNoteSending}
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleEnviarNotaDoBanner}
                        disabled={bannerNoteSending || !bannerNoteText.trim()}
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                      >
                        {bannerNoteSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (<><Lock className="h-3.5 w-3.5 mr-1" /> Salvar nota</>)}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        ) : bannerBloqueia ? (
          // Conexão Evolution em mau estado E SEM linha saudável de saída (ou grupo) —
          // substitui o composer por banner adaptativo:
          //  - isLooping: bug 428 do Evolution (>30 conn.update/min) → ação "Logout + Novo QR"
          //  - isDown:    queda estável (close consecutivo) → ação "Reiniciar" + "Novo QR"
          // Ordem importa: vem antes do is24hExpired porque queda/loop é mais grave.
          // Com linha saudável disponível o composer fica LIVRE (aviso sutil acima) —
          // o backend desvia o envio pela cadeia. (08/07)
          <EvolutionDownBanner
            connectionNome={conversation?.connection_nome || null}
            instanceName={evolutionHealth.instanceName}
            isLooping={evolutionHealth.isLooping}
            loopCount={evolutionHealth.loopCount}
            onRefresh={evolutionHealth.refresh}
          />
        ) : foraDaJanelaOficial && conversation && !isInternalMode ? (
          // Linha OFICIAL com janela de 24h fechada — só template aprovado sai.
          // Nota interna continua possível (Douglas 13/08): o botão abaixo liga o
          // modo nota e o composer normal volta (nota não vai pro cliente).
          <ComposerTemplateOficial
            conversationId={conversation.id}
            telefone={conversation.telefone}
            connectionId={conversation.connection_id || null}
            cnpj={conversation.cliente_cnpj || null}
            contatoNome={conversation.contact_name || null}
            ultimaEntrada={conversation.last_inbound_at || null}
            ultimaSaida={conversation.last_outbound_at || null}
            onNotaInterna={() => setIsInternalMode(true)}
          />
        ) : temSaidaOutraLinha ? (
          // Linha da conversa caida — o status vive SO no chip do cabecalho (1 info/1 lugar).
          // O compositor fica quieto e indisponivel (nada de caixa/alarme); a unica saida
          // natural e' abrir a conversa na linha SUA que esta no ar, com a msg pronta. Nunca
          // "pela outra fingindo". (Douglas 10/07)
          <div className="flex items-center justify-center py-1.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground"
              onClick={continuarPelaLinhaSaudavel}
            >
              Falar com o cliente pela minha {linhaSaidaFailover?.papel === 'reserva' ? 'reserva' : linhaSaidaFailover?.papel === 'secundaria' ? 'secundária' : 'outra linha'} →
            </Button>
          </div>
        ) : isRecording ? (
          /* Recording UI */
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-destructive hover:bg-destructive/10"
              onClick={cancelRecording}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <div className="flex-1 flex items-center gap-3 px-4 py-2 rounded-lg bg-destructive/5 border border-destructive/20">
              <span className="w-2.5 h-2.5 rounded-full bg-destructive animate-pulse flex-shrink-0" />
              <span className="text-sm font-mono font-medium text-foreground">
                {formatDuration(recordingDuration)}
              </span>
              <span className="text-xs text-muted-foreground">Gravando...</span>
            </div>
            <Button
              size="icon"
              className="h-9 w-9 bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={sendRecording}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-2 relative">
            {/* "Fulano está digitando..." — grupos internos */}
            {isGrupoInterno && (typingGrupo?.length || 0) > 0 && (
              <div className="text-[11px] italic text-success flex items-center gap-1.5 px-1 -mt-1">
                <span className="flex gap-0.5">
                  <span className="w-1 h-1 rounded-full bg-success animate-bounce [animation-delay:0ms]" />
                  <span className="w-1 h-1 rounded-full bg-success animate-bounce [animation-delay:120ms]" />
                  <span className="w-1 h-1 rounded-full bg-success animate-bounce [animation-delay:240ms]" />
                </span>
                {typingGrupo!.map((t) => primeiroNome(t.nome)).join(', ')}
                {typingGrupo!.length === 1 ? ' está digitando…' : ' estão digitando…'}
              </div>
            )}
            {/* Barra de "respondendo a..." — estilo WhatsApp */}
            {replyTo && (
              <div className="flex items-center gap-2 rounded-lg border-l-[3px] border-primary bg-muted/60 px-2.5 py-1.5">
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold text-primary">
                    Respondendo {replyTo.sent_by_user_id === profile?.id
                      ? 'você mesmo'
                      : (replyTo.sent_by_user_id ? (senders.get(replyTo.sent_by_user_id)?.nome || 'membro') : 'membro')}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {(replyTo.body && replyTo.body.trim()) ? replyTo.body.slice(0, 140) : '📎 Anexo'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setReplyTo(null)}
                  className="h-6 w-6 rounded-full flex items-center justify-center hover:bg-accent flex-shrink-0"
                  title="Cancelar resposta"
                >
                  <XIcon className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </div>
            )}
            {/* Autocomplete de menção @pessoa */}
            {isGrupoInterno && mentionOpen && membrosParaMencao.length > 0 && (
              <div className="absolute bottom-full left-0 mb-1 w-64 max-h-56 overflow-y-auto rounded-lg border bg-popover shadow-lg z-30 p-1">
                {membrosParaMencao.map((m) => (
                  <button
                    key={m.usuario_id}
                    type="button"
                    onClick={() => selecionarMencao(m.usuario_id, m.usuario!.nome)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent text-left"
                  >
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={m.usuario?.foto_url || undefined} />
                      <AvatarFallback className="text-[10px]">{(m.usuario?.nome || '??').slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm truncate">{m.usuario?.nome}</span>
                  </button>
                ))}
              </div>
            )}
            {/* Preview da imagem colada com Ctrl+V — Douglas 26/05.
                Aparece acima do composer. Botões Enviar / Cancelar.
                Enter no textarea também envia, Esc cancela. */}
            {pastedImage && (
              <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/[0.05] p-2">
                <img
                  src={pastedImage.previewUrl}
                  alt="Imagem colada"
                  className="h-16 w-16 object-cover rounded-md ring-1 ring-black/10"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-success dark:text-success">
                    📷 Imagem colada do clipboard
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {Math.round(pastedImage.file.size / 1024)} KB · pronta pra enviar
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground hover:text-destructive h-8"
                  onClick={handleCancelPaste}
                  disabled={isUploading}
                  title="Cancelar (Esc)"
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  className="h-8 bg-primary text-primary-foreground hover:bg-primary/90"
                  onClick={handleConfirmPasteSend}
                  disabled={isUploading}
                  title="Enviar (Enter)"
                >
                  {isUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Enviar</>}
                </Button>
              </div>
            )}
            <div className="flex flex-col gap-1.5">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
              className="hidden"
              onChange={handleFileUpload}
            />
            {/* QUEM ESCREVE, POR ONDE SAI E EM QUE MODO — a linha quente do composer.
                O Inbox tem 71 conexões e o consultor atende por várias: sem esta
                linha ele descobre por qual linha falou DEPOIS de enviar, e linha
                errada com cliente é o erro mais caro daqui.
                ⚠️ Ela e as pílulas saíram da MESMA fileira do campo de escrita —
                eram filhos do flex horizontal, junto com os dois grupos de botões,
                e espremiam a área de digitar até virar uma tira. Agora são uma
                faixa própria e o campo ocupa a largura inteira embaixo. */}
            <div className="flex items-center justify-between gap-2">
              <p className="min-w-0 flex-1 truncate text-[10px] text-muted-foreground">
                {isInternalMode ? (
                  <>Nota interna — <span className="font-semibold text-foreground">só a equipe vê</span>. O cliente não recebe.</>
                ) : connHealth?.severity === 'likely_banned' ? (
                  <span className="font-semibold text-danger">Conexão bloqueada — o envio não sai até resolver</span>
                ) : profile?.nome ? (
                  <>
                    Você responde como <span className="font-semibold text-foreground">{profile.nome.split(' ')[0]}</span>
                    {conversation?.connection_nome && (
                      <>, pela <span className="font-semibold text-foreground">{formatConnectionName(conversation.connection_nome)}</span></>
                    )}
                  </>
                ) : null}
              </p>
                {/* MENSAGEM · NOTA INTERNA — o par de pílulas do desenho aprovado
                    (inbox_centro_conversa). Era um cadeado sozinho, e quem
                    chegava na tela não sabia em que modo estava escrevendo até
                    olhar a cor do campo.
                    ⛔ E a cor era `--danger`: vermelho de FALHA, que a lei da
                    casa reserva para erro de verdade. Nota interna não é erro —
                    é outro destinatário. O acento agora é bordô, como toda
                    escolha ativa do CRM. */}
                <span className="inline-flex h-8 flex-shrink-0 items-center gap-[2px] rounded-[9px] border border-border-warm bg-muted p-[3px]">
                  {([[false, 'Mensagem'], [true, 'Nota interna']] as const).map(([modo, rotulo]) => (
                    <button
                      key={rotulo}
                      type="button"
                      onClick={() => setIsInternalMode(modo)}
                      title={modo
                        ? 'Nota interna — só a equipe vê, o cliente não recebe'
                        : 'Mensagem — vai para o cliente no WhatsApp'}
                      className={cn(
                        'flex h-[22px] items-center gap-1 rounded-[6px] px-2 text-[10px] font-bold uppercase tracking-wider leading-none transition-colors',
                        isInternalMode === modo
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {modo && <Lock className="h-2.5 w-2.5" />}
                      {rotulo}
                    </button>
                  ))}
                </span>
            </div>
            {/* Espera é dívida, e dívida é bordô. A frase diz o que está guardado
                e que ninguém precisa fazer nada — o reenvio é do CRM. */}
            {reenvioPendente && (
              <div className="mb-1 flex items-center gap-1.5 text-[11px] text-primary">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Servidor fora do ar — sua mensagem está guardada e sai sozinha quando ele voltar.</span>
              </div>
            )}
            <div className="flex items-end gap-2">
            <div className="flex gap-1 flex-shrink-0 pb-0.5">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground"
                    disabled={isUploading}
                  >
                    {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-5 w-5" />}
                  </Button>
                </PopoverTrigger>
                <PopoverContent side="top" align="start" className="w-48 p-1">
                  <button
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm hover:bg-accent transition-colors"
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.accept = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv';
                        fileInputRef.current.click();
                      }
                    }}
                  >
                    <FileUp className="h-5 w-5 text-primary" />
                    <span>Documento</span>
                  </button>
                  <button
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm hover:bg-accent transition-colors"
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.accept = 'image/*,video/*';
                        fileInputRef.current.click();
                      }
                    }}
                  >
                    <ImageIcon className="h-5 w-5 text-primary" />
                    <span>Fotos e vídeos</span>
                  </button>
                  <button
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm hover:bg-accent transition-colors"
                    onClick={startRecording}
                  >
                    <Mic className="h-5 w-5 text-success" />
                    <span>Gravar áudio</span>
                  </button>
                  <button
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm hover:bg-accent transition-colors"
                    onClick={() => setCatalogoOpen(true)}
                  >
                    <Library className="h-5 w-5 text-primary" />
                    <span>Catálogo</span>
                  </button>
                </PopoverContent>
              </Popover>
              <TemplateSelectorPopover
                conversation={conversation}
                profileId={profile?.id}
                onInsertText={(t) => setText(prev => prev + t)}
              />
            </div>
            <div className="relative flex-1 min-w-0">
            <Textarea
              ref={composerRef}
              value={text}
              onChange={handleComposerChange}
              spellCheck={false}
              lang="pt-BR"
              autoCorrect="on"
              autoCapitalize="sentences"
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={
                pastedImage
                  ? 'Imagem colada — Enter para enviar, Esc para cancelar'
                  : isInternalMode
                    ? 'Nota interna — só a equipe vê. O cliente não recebe.'
                    : connHealth?.severity === 'likely_banned' && !isInternalMode
                      ? 'Conexão bloqueada — envio indisponível até resolver com a 360dialog'
                      /* Nominal, como no desenho: "Escreva para Rochele…". Dizer
                         o nome de quem vai ler é o que evita mandar no chat
                         errado — o erro mais caro do Inbox. */
                      : `Escreva para ${(displayName || 'o cliente').split(' ')[0]}…`
              }
              className={cn(
                'block w-full min-h-[44px] max-h-[260px] overflow-y-auto px-3 py-2.5 leading-relaxed text-[14px] resize-none transition-colors',
                isInternalMode &&
                  'bg-primary/5 border-primary/30 ring-primary/20 placeholder:text-primary/70 focus-visible:ring-primary/40'
              )}
              rows={1}
            />
            <CorretorSugestoes
              textareaRef={composerRef}
              texto={text}
              correcoes={corretor.correcoes}
              onAplicar={(c, sugestao) => {
                const novo = corretor.aplicar(c, sugestao);
                const fim = c.offset + sugestao.length;
                setText(novo);
                requestAnimationFrame(() => composerRef.current?.setSelectionRange(fim, fim));
              }}
            />
            </div>
            <Button
              size="icon"
              className={cn(
                'h-9 w-9 flex-shrink-0 text-white transition-opacity',
                isInternalMode
                  ? 'bg-danger hover:bg-danger'
                  : 'bg-primary hover:bg-primary/90',
                connHealth?.severity === 'likely_banned' && !isInternalMode && 'opacity-60'
              )}
              title={
                connHealth?.severity === 'likely_banned' && !isInternalMode
                  ? 'Conexão bloqueada — envie como nota interna ou aguarde liberação'
                  : undefined
              }
              onClick={() => handleSend()}
              disabled={!text.trim() || isSending}
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
            </div>
            </div>
          </div>
        )}
      </div>
      <ImagePreviewModal
        imageUrl={previewImage?.url || null}
        imageName={previewImage?.name || ''}
        onClose={() => setPreviewImage(null)}
      />
    </div>
  );
}
